// Phase 7 v3 — Financial Performance (handoff §5 Section A-F parity).
//
// Tabs:
//   - overview     — Stoic badge + KPI sections A-F
//   - cycles       — reconstructed Wheel cycles
//   - inventory    — open assigned stock inventory with MTM
//   - import       — upload Tastytrade / Tradier / Generic CSV (dedup)
//   - transactions — normalized row audit
//   - settings     — starting capital + Stoic thresholds + per-account nicknames
//
// Admin user-switcher in the header.
// Iter119.19 — Multi-account picker (one Tastytrade login → up to 5 sub-accounts).

import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import {
  Upload, TrendingUp, TrendingDown, DollarSign, Target, Percent,
  Trash2, RefreshCw, Users, Loader2, Shield, AlertTriangle, Leaf,
  LineChart as LineIcon, FileSpreadsheet, Repeat, Boxes, Zap,
  Wallet, Pencil, Check, Link2, HelpCircle, X as XIcon, Bug,
} from "lucide-react";
import { TableExportToolbar } from "@/components/TableExportToolbar";
import FreshnessIndicator from "@/components/FreshnessIndicator";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, ReferenceDot, Legend, Cell,
} from "recharts";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const PERIODS = [
  { id: "month",   label: "Month"   },
  { id: "quarter", label: "Quarter" },
  { id: "ytd",     label: "YTD"     },
  { id: "all",     label: "All"     },
  { id: "custom",  label: "Custom"  },
];

// Iter119.19 — Sentinel for the "All accounts (combined)" pill. Sent as the
// special string "all" to the backend so it short-circuits the account filter.
const ACCT_ALL = "all";

const STOIC_COLORS = {
  harvest:  { bg: "#00FF88", text: "#08080A", icon: Leaf },
  recovery: { bg: "#FFB800", text: "#08080A", icon: RefreshCw },
  defense:  { bg: "#FF3366", text: "#FFFFFF", icon: Shield },
  neutral:  { bg: "#3BB5FF", text: "#08080A", icon: AlertTriangle },
};

export default function Performance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("overview");
  const [period, setPeriod] = useState("all");
  const [sources, setSources] = useState({ transactions: true, cycles: false });
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [targetUser, setTargetUser] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(false);
  // Iter117.5 — Track when the KPI compute last succeeded so the
  // shared FreshnessIndicator can colour-code staleness.
  const [kpisFetchedAt, setKpisFetchedAt] = useState(null);
  // Iter119.19 — Per-account state. `accounts` is the discovered list,
  // `account` is the selection (null → "All accounts" combined view).
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(() => {
    try { return localStorage.getItem(`perf.account.${user?.id || "_"}`) || null; }
    catch { return null; }
  });

  useEffect(() => {
    if (isAdmin) axios.get(`${API}/perf/users`).then(r => setAllUsers(r.data)).catch(() => {});
  }, [isAdmin]);

  const userParams = () => (targetUser && targetUser !== user?.id) ? { user_id: targetUser } : {};
  // Iter119.19 — Common scoping object: user_id (admin-switcher) + account_number.
  // Passing null/undefined for account_number omits the filter (combined view).
  const scopeParams = () => ({
    ...userParams(),
    ...(account ? { account_number: account } : {}),
  });

  // Iter119.19 — Discover sub-accounts for the (possibly admin-targeted) user.
  // Refetch whenever the user changes; persist the operator's selection in
  // localStorage so the picker remembers across reloads.
  useEffect(() => {
    axios.get(`${API}/perf/accounts`, { params: userParams() })
      .then(r => setAccounts(r.data || []))
      .catch(() => setAccounts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUser, user?.id]);

  useEffect(() => {
    try { localStorage.setItem(`perf.account.${user?.id || "_"}`, account || ""); }
    catch { /* ignore */ }
  }, [account, user?.id]);

  const fetchKpis = async () => {
    setLoading(true);
    try {
      const params = {
        period, basis: "both",
        sources: Object.entries(sources).filter(([, v]) => v).map(([k]) => k).join(",") || "transactions",
        ...(period === "custom" && customStart ? { start: customStart } : {}),
        ...(period === "custom" && customEnd ? { end: customEnd } : {}),
        ...scopeParams(),
      };
      const r = await axios.get(`${API}/perf/kpis`, { params });
      setKpis(r.data);
      setKpisFetchedAt(Date.now());
    } catch { setKpis(null); }
    setLoading(false);
  };
  useEffect(() => { fetchKpis(); /* eslint-disable-next-line */ },
    [period, JSON.stringify(sources), targetUser, account, customStart, customEnd]);

  return (
    <div data-testid="performance-page" className="animate-fade-in">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-black tracking-tighter text-white font-['Chivo']">Performance</h2>
          <p className="text-sm text-[#8A8A93] mt-1">Wheel-strategy KPIs · FIFO realised P&L · MTM inventory · Stoic Risk State.</p>
        </div>
        {isAdmin && allUsers.length > 0 && (
          <div className="flex items-center gap-2 text-xs font-mono">
            <Users size={14} className="text-[#8A8A93]" />
            <select data-testid="perf-user-switcher" value={targetUser || user?.id || ""}
              onChange={e => setTargetUser(e.target.value)}
              className="bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1 text-xs">
              {allUsers.map(u => (<option key={u.id} value={u.id}>{u.email}{u.id === user.id ? " (you)" : ""}</option>))}
            </select>
          </div>
        )}
      </div>

      {/* Iter119.19 — Account Picker pill row. Renders whenever the user has
          >=1 discovered account OR settings rows. The combined "All" pill
          always shows so the operator can fall back to the household view. */}
      {(accounts.length > 0 || account) && (
        <AccountPicker
          accounts={accounts}
          selected={account}
          onSelect={setAccount}
        />
      )}

      <div className="flex gap-0.5 border-b border-[#26262B] mb-5 flex-wrap">
        {[
          { id: "overview",    label: "Overview",     icon: LineIcon },
          { id: "cycles",      label: "Cycles",       icon: Repeat },
          { id: "inventory",   label: "Inventory",    icon: Boxes },
          { id: "import",      label: "Import",       icon: Upload },
          { id: "transactions", label: "Transactions", icon: FileSpreadsheet },
          { id: "settings",    label: "Settings",     icon: Target },
        ].map(x => (
          <button key={x.id} data-testid={`perf-tab-${x.id}`} onClick={() => setTab(x.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-[11px] font-mono font-bold uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors ${
              tab === x.id ? "border-[#00FF88] text-[#00FF88]" : "border-transparent text-[#8A8A93] hover:text-white"}`}>
            <x.icon size={13} /> {x.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <OverviewTab kpis={kpis} loading={loading} fetchKpis={fetchKpis} kpisFetchedAt={kpisFetchedAt}
          period={period} setPeriod={setPeriod} sources={sources} setSources={setSources}
          customStart={customStart} setCustomStart={setCustomStart}
          customEnd={customEnd} setCustomEnd={setCustomEnd} />
      )}
      {tab === "cycles" && <CyclesTab kpis={kpis} accountNumber={account} userParams={scopeParams} />}
      {tab === "inventory" && <InventoryTab kpis={kpis} />}
      {tab === "import" && <ImportTab refreshKpis={fetchKpis} userParams={userParams} accounts={accounts} />}
      {tab === "transactions" && <TransactionsTab userParams={scopeParams} />}
      {tab === "settings" && <SettingsTab userParams={userParams} account={account} accounts={accounts} onAccountsChanged={() => {
        axios.get(`${API}/perf/accounts`, { params: userParams() })
          .then(r => setAccounts(r.data || [])).catch(() => {});
      }} />}
    </div>
  );
}

// ===========================================================================
// Iter119.19 — Account Picker (pill row)
// ===========================================================================
//
// Renders one pill per discovered Tastytrade sub-account plus a synthetic
// "All accounts" pill. Each pill shows the operator-chosen nickname (if
// set) or a masked account number, plus a tiny "N tx" badge. The active
// pill glows cyan; the rest are dim grey.
//
// Storage: parent component persists the selected account_number in
// localStorage `perf.account.{userId}`.
function AccountPicker({ accounts, selected, onSelect }) {
  // Mask 5-digit-or-longer account numbers as "•••12345" so screenshots
  // don't leak full account IDs.
  const mask = (acct) => {
    if (!acct) return "Unassigned";
    const s = String(acct);
    return s.length > 5 ? `•••${s.slice(-5)}` : s;
  };
  const pill = (key, label, count, active, testid, onClick) => (
    <button
      key={key}
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border text-[11px] font-mono transition-colors ${
        active
          ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10"
          : "border-[#26262B] text-[#8A8A93] hover:text-white hover:border-[#5F5F66]"
      }`}
    >
      <Wallet size={11} className={active ? "text-[#00FF88]" : "text-[#5F5F66]"} />
      <span className="font-bold uppercase tracking-[0.12em]">{label}</span>
      {count !== undefined && (
        <span className={`text-[9px] tracking-normal normal-case px-1.5 py-0.5 rounded-sm ${
          active ? "bg-[#00FF88]/20 text-[#00FF88]" : "bg-[#1A1A20] text-[#5F5F66]"
        }`}>{count} tx</span>
      )}
    </button>
  );
  const totalTx = accounts.reduce((s, a) => s + (a.txn_count || 0), 0);
  return (
    <div data-testid="perf-account-picker" className="mb-5 flex items-center gap-2 flex-wrap">
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5F5F66] mr-1">Account</span>
      {pill("__all__", "All accounts", totalTx, !selected, "perf-account-pill-all", () => onSelect(null))}
      {accounts.map(a => {
        const key = a.account_number || "_unassigned";
        const label = a.nickname || mask(a.account_number);
        const isSel = (a.account_number ? selected === a.account_number : selected === "unassigned");
        return pill(
          key,
          label,
          a.txn_count,
          isSel,
          `perf-account-pill-${a.account_number || "unassigned"}`,
          () => onSelect(a.account_number || "unassigned"),
        );
      })}
    </div>
  );
}

// ===========================================================================
// Overview tab — Sections A-F per handoff
// ===========================================================================
function OverviewTab({ kpis, loading, fetchKpis, kpisFetchedAt, period, setPeriod, sources, setSources,
  customStart, setCustomStart, customEnd, setCustomEnd }) {
  const k = kpis?.kpis || {};
  const stoic = kpis?.stoic;
  const eq = kpis?.equity_curve || [];
  const cash = kpis?.cashflow || {};
  const byMonth = kpis?.by_month || [];
  const bySym = kpis?.by_symbol || [];
  const conc = kpis?.concentration;

  return (
    <div className="space-y-5">
      {/* Period / source controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1">
          {PERIODS.map(p => (
            <button key={p.id} data-testid={`perf-period-${p.id}`} onClick={() => setPeriod(p.id)}
              className={`text-[11px] font-mono px-2.5 py-1 rounded-sm border ${period === p.id ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}>{p.label}</button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex items-center gap-2 text-[11px] font-mono">
            <input type="date" data-testid="perf-custom-start" value={customStart?.slice(0, 10) || ""}
              onChange={e => setCustomStart(e.target.value ? `${e.target.value}T00:00:00Z` : "")}
              className="bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1" />
            <span className="text-[#5F5F66]">→</span>
            <input type="date" data-testid="perf-custom-end" value={customEnd?.slice(0, 10) || ""}
              onChange={e => setCustomEnd(e.target.value ? `${e.target.value}T23:59:59Z` : "")}
              className="bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1" />
          </div>
        )}
        <div className="ml-auto">
          {/* Iter117.5 — KPI freshness + Refresh via shared component. */}
          <FreshnessIndicator
            fetchedAt={kpisFetchedAt}
            onRefresh={fetchKpis}
            loading={loading}
            testidPrefix="perf-kpis"
            refreshTitle="Re-compute KPIs"
          />
        </div>
      </div>

      {/* Section F — Stoic Risk State (top-of-page) */}
      {stoic && <StoicBadge stoic={stoic} />}

      {/* Section A — Account Truth */}
      <SectionHeader title="A · Account Truth" subtitle="What the account actually did" />
      <div data-testid="perf-kpi-grid" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Closed Realized Strategy P&L" value={fmtUsd(k.closed_realized_strategy_pnl)}
          testid="kpi-closed-realized-strategy-pnl" tone={toneByValue(k.closed_realized_strategy_pnl)} icon={DollarSign}
          hint={`${kpis?.counts?.csp_closes || 0} CSP · ${kpis?.counts?.cc_closes || 0} CC closed`}
          tooltip="Realized premium + realized stock + realized other-option P&L. Closed activity only — does not include open MTM." />
        <KpiCard label="Realized Premium P&L" value={fmtUsd(k.realized_premium_pnl)}
          testid="kpi-realized-premium-pnl" tone={toneByValue(k.realized_premium_pnl)} icon={Leaf}
          hint="CSP + CC premiums kept"
          tooltip="Σ FIFO realized P&L for closed CSP and CC legs only. Net of buybacks, rolls, fees, commissions." />
        <KpiCard label="Realized Stock Leg P&L" value={fmtUsd(k.realized_stock_leg_pnl)}
          testid="kpi-realized-stock-leg-pnl" tone={toneByValue(k.realized_stock_leg_pnl)} icon={Target}
          hint="Assigned shares after sell" />
        <KpiCard label="Open MTM vs Effective $" value={fmtUsd(k.open_unrealised_strategy)}
          testid="kpi-open-unrealised-strategy" tone={toneByValue(k.open_unrealised_strategy)} icon={TrendingUp}
          hint="vs strategy basis"
          tooltip="Σ (spot − Effective $) × shares across open inventory. Mark-to-market against Wheel economic break-even (Effective $), NOT raw broker basis." />
        <KpiCard label="Open MTM vs Accounting $" value={fmtUsd(k.open_unrealised_accounting)}
          testid="kpi-open-unrealised-accounting" tone={toneByValue(k.open_unrealised_accounting)} icon={TrendingUp}
          hint="vs raw broker basis"
          tooltip="Σ (spot − Accounting $) × shares. Broker-view MTM — what your platform would show as unrealised P&L on assigned lots." />
        <KpiCard label="Estimated Equity" value={fmtUsd(kpis?.nlv)}
          testid="kpi-nlv" tone="neutral" icon={DollarSign}
          hint="Closed equity + open stock MTM"
          tooltip="Realized equity + Open MTM on shares. Does NOT mark open options to market (option chain quotes not currently subscribed). True NLV requires live option marks; this is the operator-friendly estimate." />
        <KpiCard label="Starting Capital" value={fmtUsd(kpis?.starting_capital)}
          testid="kpi-starting-capital" tone="neutral" icon={DollarSign} hint="Set in Settings" />
        <KpiCard label="Max Drawdown" value={fmtPct(k.max_drawdown_pct)}
          testid="kpi-drawdown" icon={TrendingDown}
          tone={k.max_drawdown_pct > 20 ? "red" : k.max_drawdown_pct > 10 ? "yellow" : "green"} />
      </div>

      {/* Section B — Wheel Income Engine */}
      <SectionHeader title="B · Wheel Income Engine" subtitle="Premium collection efficiency" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Gross Premium Written" value={fmtUsd(k.gross_premium)}
          testid="kpi-gross-premium" tone={toneByValue(k.gross_premium)} icon={DollarSign}
          hint="Diagnostic only"
          tooltip="Σ STO credits opened (raw short-option premium received). DIAGNOSTIC — not all of this is retained. See Net Premium Captured for economic income." />
        <KpiCard label="Net Premium Captured" value={fmtUsd(k.net_premium_captured)}
          testid="kpi-net-premium-captured" tone={toneByValue(k.net_premium_captured)} icon={Leaf}
          hint="FIFO realized"
          tooltip="Σ FIFO realized P&L for closed CSP and CC legs only. Net of buybacks, rolls, fees, commissions. This is real economic income — Iter119.32 doctrine." />
        <KpiCard label="Premium Capture Rate" value={fmtPct(k.premium_capture_rate_pct)}
          testid="kpi-capture-rate" tone={k.premium_capture_rate_pct >= 70 ? "green" : k.premium_capture_rate_pct >= 50 ? "yellow" : "red"}
          icon={Percent} hint="Net ÷ Gross"
          tooltip="Net Premium Captured ÷ Gross Premium Written × 100. How much of every dollar opened actually banked." />
        <KpiCard label="Fee Drag on Premium" value={fmtPct(k.fee_drag_on_premium_pct)}
          testid="kpi-fee-drag" tone={k.fee_drag_on_premium_pct > 5 ? "red" : "neutral"} icon={Percent}
          tooltip="Total option commissions + fees ÷ Gross Premium Written × 100. Above 5% means commissions are eating the edge." />
        <KpiCard label="Avg ROI / CSP" value={fmtPct(k.avg_roi_csp_pct)}
          testid="kpi-roi-csp" tone={toneByValue(k.avg_roi_csp_pct)} icon={Target}
          hint={`Capital-weighted: ${fmtPct(k.weighted_roi_csp_pct)}`}
          tooltip="Leg-level diagnostic. Arithmetic mean of realized_pnl / (strike × 100) across closed CSP legs. Use Cycle ROI (Section D) for strategic truth." />
        <KpiCard label="Avg ROI / CC" value={fmtPct(k.avg_roi_cc_pct)}
          testid="kpi-roi-cc" tone={toneByValue(k.avg_roi_cc_pct)} icon={Target}
          hint={`Capital-weighted: ${fmtPct(k.weighted_roi_cc_pct)}`}
          tooltip="Leg-level diagnostic. A losing CC leg can be tactical defense — cycle-level P&L is the strategic truth." />
        <KpiCard label="Realized Dietz" value={fmtPct(k.account_roi_dietz_pct)}
          testid="kpi-roi-dietz" tone={toneByValue(k.account_roi_dietz_pct)} icon={Percent}
          hint="Closed P&L only"
          tooltip="Modified Dietz on CLOSED P&L only. What the account has actually realized (banked)." />
        <KpiCard label="Realized $-Day" value={fmtPct(k.account_roi_dollar_day_pct)}
          testid="kpi-roi-dollar-day" tone={toneByValue(k.account_roi_dollar_day_pct)} icon={Percent}
          hint="Per-deployed-dollar-day"
          tooltip="$-Day ROI on CLOSED P&L only. Adjusts realized return by daily deployed capital balance." />
        <KpiCard label="Economic Dietz" value={fmtPct(k.economic_dietz_roi_pct)}
          testid="kpi-economic-dietz" tone={toneByValue(k.economic_dietz_roi_pct)} icon={Percent}
          hint="Realized + Open MTM"
          tooltip="Iter119.32 — Audit §4.3. (closed P&L + open strategy MTM) ÷ weighted capital. Answers 'where does the account stand economically right now', including open inventory." />
        <KpiCard label="Economic $-Day" value={fmtPct(k.economic_dollar_day_roi_pct)}
          testid="kpi-economic-dollar-day" tone={toneByValue(k.economic_dollar_day_roi_pct)} icon={Percent}
          hint="Realized + Open MTM"
          tooltip="Iter119.32 — Audit §4.3. (closed P&L + open strategy MTM) ÷ avg daily capital balance. Includes open Wheel inventory in the return calc." />
      </div>

      {/* Section C — Open Risk */}
      <SectionHeader title="C · Open Risk / Inventory" subtitle="What's still at risk — shares AND open CSPs" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Assigned Share Exposure" value={fmtPct(k.assigned_share_exposure_pct ?? k.open_exposure_pct)}
          testid="kpi-open-exposure" icon={Shield}
          tone={(k.assigned_share_exposure_pct ?? k.open_exposure_pct) > 65 ? "red" : (k.assigned_share_exposure_pct ?? k.open_exposure_pct) > 35 ? "yellow" : "green"}
          hint="Assigned capital ÷ NLV"
          tooltip="Capital already converted into assigned shares ÷ Estimated Equity × 100. Iter119.32 — Audit §4.4 split. Excludes open CSP notional." />
        <KpiCard label="Total Wheel Capital at Risk" value={fmtPct(k.total_wheel_capital_at_risk_pct)}
          testid="kpi-wheel-cap-at-risk" icon={Shield}
          tone={k.total_wheel_capital_at_risk_pct > 75 ? "red" : k.total_wheel_capital_at_risk_pct > 45 ? "yellow" : "green"}
          hint={`Inc. ${fmtUsd(k.open_csp_notional)} open CSP`}
          tooltip="(assigned share capital + open CSP cash-secured notional) ÷ Estimated Equity × 100. TRUE wheel commitment per Iter119.32 doctrine. Includes unassigned CSPs which still tie up capital." />
        <KpiCard label="Open CSP Notional" value={fmtUsd(k.open_csp_notional)}
          testid="kpi-open-csp-notional" icon={DollarSign}
          tone={k.open_csp_notional > 0 ? "yellow" : "neutral"}
          hint="Σ strike × 100 × open contracts"
          tooltip="Cash-secured commitment on open short PUTS (calls not counted — shares are their collateral). This capital is at risk of being called for assignment any time before expiry." />
        <KpiCard label="Largest Ticker" value={fmtPct(k.largest_ticker_with_csp_pct ?? k.largest_ticker_pct)}
          testid="kpi-largest-ticker" icon={AlertTriangle}
          tone={(k.largest_ticker_with_csp_pct ?? k.largest_ticker_pct) > 20 ? "red" : (k.largest_ticker_with_csp_pct ?? k.largest_ticker_pct) > 10 ? "yellow" : "neutral"}
          hint="Inc. open CSPs"
          tooltip="Max (per-symbol assigned capital + per-symbol open CSP notional) ÷ Estimated Equity × 100. Concentration risk across assigned shares AND open CSPs." />
        <KpiCard label="Assigned Capital" value={fmtUsd(k.assigned_capital_total)}
          testid="kpi-assigned-capital" tone="neutral" icon={DollarSign}
          tooltip="Total $ tied up in currently-assigned shares (excludes open CSP commitments)." />
        <KpiCard label="Realized ÷ Open Risk" value={k.realised_to_open_ratio ? `${k.realised_to_open_ratio}x` : "—"}
          testid="kpi-realised-to-open" icon={Target}
          tone={k.realised_to_open_ratio >= 1 ? "green" : k.realised_to_open_ratio >= 0.5 ? "yellow" : "red"}
          hint="Cushion ratio"
          tooltip="Closed Realized Strategy P&L ÷ |Open Unrealised Strategy| (only when open is negative). 'Can my realized cushion absorb the open losses if I close everything?'" />
      </div>

      {/* Section D — Closed Cycle Quality */}
      <SectionHeader title="D · Closed Cycle Quality" subtitle="How efficiently the wheel runs" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Avg ROI / Cycle" value={fmtPct(k.avg_roi_cycle_pct)}
          testid="kpi-roi-cycle" tone={toneByValue(k.avg_roi_cycle_pct)} icon={Repeat}
          hint={`${kpis?.counts?.cycles_closed || 0} closed`} />
        <KpiCard label="Win Rate" value={fmtPct(k.win_rate_pct)}
          testid="kpi-winrate" tone={toneByValue(k.win_rate_pct)} icon={TrendingUp} />
        <KpiCard label="CSP Assignment Rate" value={fmtPct(k.csp_assignment_rate_pct)}
          testid="kpi-assignment-rate" icon={AlertTriangle}
          tone={k.csp_assignment_rate_pct > 50 ? "red" : k.csp_assignment_rate_pct > 20 ? "yellow" : "green"} />
        <KpiCard label="Avg Days in CSP" value={k.avg_days_in_csp != null ? `${k.avg_days_in_csp}d` : "—"}
          testid="kpi-days-csp" tone="neutral" icon={Target} />
        <KpiCard label="Avg Days in Recovery" value={k.avg_days_in_recovery != null ? `${k.avg_days_in_recovery}d` : "—"}
          testid="kpi-days-recovery" tone="neutral" icon={RefreshCw} />
        <KpiCard label="Avg Days / Cycle" value={k.avg_days_in_cycle != null ? `${k.avg_days_in_cycle}d` : "—"}
          testid="kpi-days-cycle" tone="neutral" icon={Repeat} />
        <KpiCard label="Capital Recycling Speed" value={k.capital_recycling_speed != null ? `${k.capital_recycling_speed}x` : "—"}
          testid="kpi-recycling" tone="neutral" icon={RefreshCw} hint="Cycles per year" />
        <KpiCard label="Open Cycles" value={kpis?.counts?.cycles_open || 0}
          testid="kpi-open-cycles" tone="neutral" icon={Repeat} />
      </div>

      {/* Equity curve */}
      <Section title={`Equity Curve · ${eq.length} days · ${(cash.events || []).length} cash-flow events`}>
        {eq.length > 1 ? (
          <div data-testid="perf-equity-chart" style={{ width: "100%", minWidth: 320, height: 320, minHeight: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={eq} margin={{ top: 5, right: 24, bottom: 4, left: 4 }}>
                <CartesianGrid stroke="#1A1A20" strokeDasharray="2 4" />
                <XAxis dataKey="date" stroke="#5F5F66" style={{ fontSize: "10px", fontFamily: "monospace" }} />
                <YAxis stroke="#5F5F66" style={{ fontSize: "10px", fontFamily: "monospace" }} tickFormatter={v => fmtUsd(v, 0)} />
                <RTooltip contentStyle={{ background: "#08080A", border: "1px solid #26262B", fontFamily: "monospace", fontSize: "11px" }}
                  labelStyle={{ color: "#8A8A93" }} formatter={v => fmtUsd(v)} />
                <Line type="monotone" dataKey="equity" stroke="#00FF88" strokeWidth={1.8} dot={false} name="Realized Equity" />
                <Line type="monotone" dataKey="mtm_equity" stroke="#3BB5FF" strokeWidth={1.6} dot={false} name="MTM Equity" />
                <Line type="monotone" dataKey="deposits_cum" stroke="#FFB800" strokeWidth={1.1} dot={false} strokeDasharray="2 4" name="Cumul. deposits" />
                {(cash.events || []).map((c, i) => {
                  const point = eq.find(p => p.date === c.date);
                  if (!point) return null;
                  return <ReferenceDot key={i} x={c.date} y={point.equity}
                    r={4} fill={c.kind === "DEPOSIT" ? "#FFB800" : "#FF3366"} stroke="#0A0A0D" strokeWidth={1.5} />;
                })}
                <Legend wrapperStyle={{ fontSize: "10px", fontFamily: "monospace", color: "#8A8A93" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : <Empty>Upload broker transactions to plot the equity curve.</Empty>}
      </Section>

      {/* Monthly P&L (Iter120.6 — sign-aware coloring + theme picker) */}
      <Section title={`Monthly P&L · ${byMonth.length} months`} extras={<MonthlyPnlThemePicker />}>
        {byMonth.length > 0 ? (
          <MonthlyPnlChart data={byMonth} />
        ) : <Empty>No monthly data yet.</Empty>}
      </Section>

      {/* Section E — Cash & Reconciliation */}
      <SectionHeader title="E · Cash & Reconciliation" subtitle="Where the cash came from" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Deposits" value={fmtUsd(cash.deposits)}
          testid="kpi-deposits" tone="neutral" icon={DollarSign}
          hint={`${cash.deposit_count || 0} events`} />
        <KpiCard label="Withdrawals" value={fmtUsd(cash.withdrawals)}
          testid="kpi-withdrawals" tone="neutral" icon={DollarSign}
          hint={`${cash.withdrawal_count || 0} events`} />
        <KpiCard label="Net External Cash Flow" value={fmtUsd(cash.net_external_cashflow)}
          testid="kpi-net-external" tone={toneByValue(cash.net_external_cashflow)} icon={DollarSign} />
        <KpiCard label="Non-Trading Adjustments" value={fmtUsd(cash.non_trading_adjustments)}
          testid="kpi-non-trading" tone="neutral" icon={DollarSign}
          hint="Dividends · interest · fees" />
      </div>

      {/* Per-symbol */}
      <Section title={`Per-Symbol P&L · ${bySym.length} underlyings · largest ${fmtPct(conc?.largest_ticker_pct)}`}>
        {bySym.length > 0 ? (
          <div data-testid="perf-symbol-table" className="overflow-x-auto">
            <table className="w-full text-[11px] font-mono">
              <thead>
                <tr className="text-[#8A8A93] border-b border-[#26262B] uppercase tracking-[0.15em]">
                  <th className="text-left px-3 py-2">Symbol</th>
                  <th className="text-right px-3 py-2">Trades</th>
                  <th className="text-right px-3 py-2">Premium+Stock</th>
                  <th className="text-right px-3 py-2">Other</th>
                  <th className="text-right px-3 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {bySym.map((b, i) => (
                  <tr key={`${b.symbol}-${i}`} className="border-b border-[#1A1A20] hover:bg-[#0A0A0D]">
                    <td className="px-3 py-1.5 text-[#00FF88] font-bold">{b.symbol}</td>
                    <td className="px-3 py-1.5 text-right text-white">{b.trades}</td>
                    <td className={`px-3 py-1.5 text-right ${b.wheel_pnl > 0 ? "text-[#00FF88]" : b.wheel_pnl < 0 ? "text-[#FF3366]" : "text-[#5F5F66]"}`}>{fmtUsd(b.wheel_pnl)}</td>
                    <td className={`px-3 py-1.5 text-right ${b.nonwheel_pnl > 0 ? "text-[#00FF88]" : b.nonwheel_pnl < 0 ? "text-[#FF3366]" : "text-[#5F5F66]"}`}>{fmtUsd(b.nonwheel_pnl)}</td>
                    <td className={`px-3 py-1.5 text-right font-bold ${b.realized_pnl > 0 ? "text-[#00FF88]" : "text-[#FF3366]"}`}>{fmtUsd(b.realized_pnl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty>No realised P&L yet.</Empty>}
      </Section>
    </div>
  );
}

// ===========================================================================
// Stoic Risk Badge
// ===========================================================================
function StoicBadge({ stoic }) {
  const c = STOIC_COLORS[stoic.state] || STOIC_COLORS.neutral;
  const Icon = c.icon;
  return (
    <div data-testid="perf-stoic-badge"
      className="border-2 rounded-sm p-4 flex items-start gap-4"
      style={{ borderColor: c.bg, background: `${c.bg}10` }}>
      <div className="flex-shrink-0 p-3 rounded-sm" style={{ background: c.bg, color: c.text }}>
        <Icon size={28} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#8A8A93]">Stoic Risk State</span>
          <span className="text-[11px] font-mono text-[#5F5F66]">
            exposure {Number(stoic.exposure_pct || 0).toFixed(1)}% · drawdown {Number(stoic.drawdown_pct || 0).toFixed(1)}%
          </span>
        </div>
        <div className="text-2xl font-black tracking-tight" style={{ color: c.bg }}>{stoic.badge}</div>
        <div className="text-xs text-[#E4E4E7] mt-1">{stoic.meaning}</div>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div>
            <div className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#00FF88] mb-1">✓ Allowed</div>
            {(stoic.allowed || []).map((a, i) => <div key={i} className="text-[11px] text-[#E4E4E7]">• {a}</div>)}
          </div>
          <div>
            <div className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#FF3366] mb-1">✕ Forbidden</div>
            {(stoic.forbidden || []).length === 0 && <div className="text-[11px] text-[#5F5F66]">—</div>}
            {(stoic.forbidden || []).map((a, i) => <div key={i} className="text-[11px] text-[#E4E4E7]">• {a}</div>)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Iter119.30 — Wheel ITM Portfolio Aggregate Card
// One-glance "where am I on the wheel right now" summary that sits above
// the per-ticker Inventory table. Aggregates basis-weighted recovery
// across every currently-open assigned position.
// ===========================================================================

// Iter119.31 — Methodology drawer. Renders the same content as
// /app/docs/WHEEL_RISK_SNAPSHOT_FORMULAS.md but in a side-panel so the
// operator can audit a number without leaving the page. Bundled as a
// JS data structure (not fetched) — zero network calls, instant open.
const WHEEL_METHODOLOGY = [
  {
    title: "Per-ticker rollup columns",
    rows: [
      {
        name: "Accounting $",
        formula: "basis_dollars / shares",
        gloss: "Raw assigned price per share — what your broker shows.",
      },
      {
        name: "Effective $",
        formula: "(basis_dollars − net_option_cashflow) / shares",
        gloss: "Strategy break-even per share after netting every premium dollar collected (rolls + fees + open-short credit). Iter119.29 doctrine.",
      },
      {
        name: "Conserv. BE",
        formula: "(basis_dollars − (net_option_cashflow − open_short_credit)) / shares",
        gloss: "Same as Effective $ but excludes credit from still-open short options. Always ≥ Effective $.",
      },
      {
        name: "Distance BE %",
        formula: "(spot − effective_basis) / effective_basis × 100",
        gloss: "Positive = profitable vs strategy basis. Uses Effective $.",
      },
      {
        name: "MTM Strategy",
        formula: "(spot − effective_basis) × shares",
        gloss: "Mark-to-market vs premium-adjusted basis.",
      },
      {
        name: "MTM Accounting",
        formula: "(spot − accounting_basis) × shares",
        gloss: "Mark-to-market vs raw assigned basis (broker view).",
      },
      {
        name: "Cycle Days",
        formula: "(now − min(assigned_dates)).days",
        gloss: "Calendar days since the FIRST assignment opened this cycle. Drives every velocity / annualisation metric.",
      },
      {
        name: "Velocity $/d",
        formula: "net_option_cashflow / cycle_days",
        gloss: "Avg $/day of premium harvest. Positive = collecting; negative = defending. None when cycle_days = 0.",
      },
      {
        name: "Repair %",
        formula: "(net_option_cashflow / basis_dollars) × 100",
        gloss: "Fraction of the share basis recovered through premium. 100% = fully recovered, >100% = profitable wheel.",
      },
      {
        name: "Cycle ROI",
        formula: "repair_pct × (365 / cycle_days)",
        gloss: "Annualised version of Repair %. Use for cross-strategy comparison on a yearly basis.",
      },
      {
        name: "Projected $",
        formula: "velocity × dte_next_open_short",
        gloss: "Forward dollar projection at current pace through the nearest expiry on this position. None when no open short.",
      },
      {
        name: "Days → Repair",
        formula: "(basis_dollars − net_option_cashflow) / velocity",
        gloss: "Days needed to fully repair the basis at current velocity. None when velocity ≤ 0.",
      },
    ],
  },
  {
    title: "Portfolio Aggregate Card tiles",
    rows: [
      {
        name: "Open Share Cost",
        formula: "Σ (accounting_basis × shares) across every open row",
        gloss: "Total capital locked up in assigned stock right now.",
      },
      {
        name: "Net Premium Banked",
        formula: "Σ net_option_cashflow",
        gloss: "Portfolio-level net cashflow. Positive = wheel collecting; negative = defending.",
      },
      {
        name: "Weighted Repair %",
        formula: "(total_net_premium / total_open_cost) × 100",
        gloss: "Capital-weighted recovery rate. Naturally weights each position by its share basis.",
      },
      {
        name: "Weighted Eff. $",
        formula: "Σ (effective_basis × shares) / Σ shares",
        gloss: "Share-weighted blended break-even price across every open ticker.",
      },
      {
        name: "Open Shortfall",
        formula: "total_open_share_cost − total_net_premium_banked",
        gloss: "Total $ still needed for the whole portfolio to fully break even at current sizes.",
      },
      {
        name: "Projected $ to Expiry",
        formula: "Σ projected_dollars per row",
        gloss: "Sum of per-ticker forward projections — each row uses ITS OWN nearest DTE.",
      },
      {
        name: "Open Positions",
        formula: "count(rows in inventory)",
        gloss: "Number of underlyings with assigned shares currently held.",
      },
    ],
  },
  {
    title: "Edge cases & guard rails",
    rows: [
      { name: "Assigned today (cycle_days = 0)", formula: "—", gloss: "Velocity / ROI / Projected $ / Days→Repair all return None. Repair % still defined." },
      { name: "No open short options", formula: "—", gloss: "DTE = None → Projected $ = None. Velocity still defined from historical cashflow." },
      { name: "Negative velocity (defending)", formula: "—", gloss: "Days→Repair = None (would be infinite). Velocity itself is reported as negative $/day so burn rate is visible." },
      { name: "Already net-positive (repair > 100%)", formula: "—", gloss: "Effective $ goes NEGATIVE. Days→Repair = None (shortfall is non-positive)." },
      { name: "Empty inventory", formula: "—", gloss: "Aggregate returns zeros for $ totals, None for weighted means. No div-by-zero." },
      { name: "Missing spot quote", formula: "—", gloss: "Open Market Forge for the symbol once to populate the chart cache." },
    ],
  },
  {
    title: "Wheel Cycle Locked vs Strategy split (Iter119.34)",
    rows: [
      { name: "Locked Effective BE", formula: "(basis − LOCKED option premium) / shares", gloss: "PRIMARY break-even. Excludes credit still on open shorts. Use for capital-preservation decisions." },
      { name: "Effective BE incl. Open", formula: "(basis − net option cashflow) / shares", gloss: "SECONDARY break-even. Includes open short credit as if it were already locked. Always ≤ Locked BE." },
      { name: "Locked Option Premium", formula: "net cashflow − open_short_credit", gloss: "Real banked option premium. The dollars that have actually hardened into capital recovery." },
      { name: "Open Short Credit", formula: "Σ remaining credit on open short lots", gloss: "Capital still at risk — could harden (if expires worthless) or disappear (if BTC'd above credit)." },
      { name: "Locked Repair %", formula: "LOCKED premium ÷ basis × 100", gloss: "PRIMARY recovery rate. Real wound healing. Capital actually recovered through banked premium." },
      { name: "Strategy Repair %", formula: "net cashflow ÷ basis × 100", gloss: "SECONDARY recovery rate. Includes open credit. Always ≥ Locked Repair %." },
      { name: "Assigned Repair Velocity", formula: "LOCKED premium since assignment ÷ cycle days", gloss: "PRIMARY velocity. Only locked closed legs after assignment. Replaces the old velocity = total cashflow / days." },
      { name: "Total Cycle Velocity", formula: "net cashflow ÷ days_since_first_cycle_sto", gloss: "Diagnostic only. Full cycle pace from the first STO of the cycle (pre- or post-assignment)." },
      { name: "Ann. Locked Repair Run-Rate", formula: "Locked Repair % × (365 / cycle_days)", gloss: "Diagnostic, NOT a forecast. Warning when cycle_days < 7 — annualisation unstable on young cycles." },
      { name: "Open Premium at Stake", formula: "= open_short_credit", gloss: "Replaces the old `projected $ = velocity × DTE` fiction. This is real risk dollars on the open contracts." },
      { name: "Max Additional Locked Premium", formula: "= open_short_credit (if all expire worthless)", gloss: "Maximum, NOT a forecast. Real amount = open_short_credit − current_buyback_cost (requires live option marks)." },
      { name: "Run-Rate Days to Full Repair", formula: "locked_shortfall ÷ assigned_repair_velocity", gloss: "Diagnostic projection at the CURRENT locked velocity. Only defined when velocity is strictly positive." },
      { name: "Early-Cycle Warning", formula: "cycle_days < 7", gloss: "Flags positions where annualisation is unstable. Annualised numbers must NOT be used as headline performance." },
    ],
  },
  {
    title: "Account-level KPIs (Iter119.32 — Economic Truth)",    rows: [
      {
        name: "Gross Premium Written",
        formula: "Σ STO credits opened (raw)",
        gloss: "DIAGNOSTIC only. Raw short-option premium received. NOT economic income — much of it gets paid back on closes/rolls.",
      },
      {
        name: "Net Premium Captured",
        formula: "Σ FIFO realized P&L for closed CSP+CC legs",
        gloss: "ECONOMIC TRUTH. Net of buybacks, rolls, fees, commissions. This is what actually banked.",
      },
      {
        name: "Premium Capture Rate",
        formula: "Net Premium Captured ÷ Gross Premium Written × 100",
        gloss: "How much of every opened premium dollar actually banked. Strategy efficiency metric.",
      },
      {
        name: "Realized Dietz",
        formula: "Modified Dietz on CLOSED P&L only",
        gloss: "Time-weighted return based purely on banked P&L. 'What's been earned'.",
      },
      {
        name: "Economic Dietz",
        formula: "(closed P&L + open strategy MTM) ÷ weighted_capital × 100",
        gloss: "Audit §4.3. Includes open inventory MTM vs Effective $. 'Where the account stands economically right now'.",
      },
      {
        name: "Realized $-Day",
        formula: "Closed P&L ÷ Σ (capital × days_deployed) × 100",
        gloss: "Per-deployed-dollar-day yield. Realized basis.",
      },
      {
        name: "Economic $-Day",
        formula: "(closed + open MTM) ÷ avg daily capital × 100",
        gloss: "Same as Realized $-Day but includes open MTM. Comparable across time as new inventory rotates.",
      },
      {
        name: "Assigned Share Exposure %",
        formula: "Σ assigned_capital ÷ Estimated Equity × 100",
        gloss: "Audit §4.4 split. Only counts capital ALREADY converted into stock. Excludes open CSP commitments.",
      },
      {
        name: "Open CSP Notional",
        formula: "Σ (strike × 100 × open_contracts) for short PUTS only",
        gloss: "Cash-secured commitment that hasn't been assigned yet. Short CALLS not counted (shares are their collateral).",
      },
      {
        name: "Total Wheel Capital at Risk %",
        formula: "(assigned_capital + open_csp_notional) ÷ Estimated Equity × 100",
        gloss: "TRUE wheel commitment. Includes both assigned-shares AND unassigned CSPs. Use this for portfolio sizing decisions.",
      },
      {
        name: "Largest Ticker (inc. CSPs)",
        formula: "max(per-symbol assigned + per-symbol open CSP) ÷ Estimated Equity × 100",
        gloss: "Concentration risk across BOTH assigned shares AND open CSPs for the same underlying.",
      },
      {
        name: "Estimated Equity",
        formula: "last realized equity + open share MTM (NOT options)",
        gloss: "Operator-friendly NLV estimate. Does NOT mark open options (chain quotes not subscribed). True NLV requires live option marks.",
      },
    ],
  },
];

function MethodologyDrawer({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      data-testid="wheel-methodology-drawer"
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="wheel-methodology-backdrop"
      />
      {/* drawer */}
      <div className="ml-auto relative w-full sm:w-[640px] h-full bg-[#08080A] border-l border-[#26262B] flex flex-col">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#26262B] bg-[#0F0F14]">
          <Boxes size={16} className="text-[#00FF88]" />
          <div className="flex-1">
            <div className="text-[12px] font-mono font-bold uppercase tracking-[0.18em] text-white">
              Wheel Risk Snapshot · Methodology
            </div>
            <div className="text-[10px] font-mono text-[#5F5F66] mt-0.5">
              Formula reference for every metric on the Inventory tab
            </div>
          </div>
          <button
            data-testid="wheel-methodology-close"
            onClick={onClose}
            className="text-[#8A8A93] hover:text-white p-1 rounded-sm hover:bg-[#26262B]"
            aria-label="Close methodology drawer"
          >
            <XIcon size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6 text-[11px] font-mono">
          <div className="bg-[#121216] border border-[#26262B] rounded-sm p-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#8A8A93] mb-1.5">
              Doctrinal foundation
            </div>
            <div className="text-[#E0E0E5] leading-relaxed">
              Every metric below is derived from a per-symbol cashflow walker that
              tracks the <em>active cycle</em> (resets whenever shares fully exit).
              While shares are open it accumulates <span className="text-[#3BB5FF]">basis_dollars</span>,
              <span className="text-[#3BB5FF]"> net_option_cashflow</span> (signed Σ of every option amount
              minus fees), and a FIFO stack of currently-open short options
              (puts AND calls) with their per-contract premium + expiration.
              Full doc on disk at <span className="text-[#FFB800]">/app/docs/WHEEL_RISK_SNAPSHOT_FORMULAS.md</span>.
            </div>
          </div>
          {WHEEL_METHODOLOGY.map((section) => (
            <div key={section.title} data-testid={`methodology-section-${section.title.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="text-[11px] font-mono font-bold uppercase tracking-[0.18em] text-[#00FF88] mb-2">
                {section.title}
              </div>
              <div className="space-y-2">
                {section.rows.map((row) => (
                  <div
                    key={row.name}
                    className="bg-[#0F0F14] border border-[#26262B] rounded-sm p-3 hover:border-[#3F3F47]"
                  >
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <div className="text-white font-bold">{row.name}</div>
                      {row.formula !== "—" && (
                        <div className="text-[10px] text-[#3BB5FF] text-right break-all">
                          {row.formula}
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] text-[#8A8A93] leading-relaxed">
                      {row.gloss}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="bg-[#0F0F14] border border-[#3BB5FF]/40 rounded-sm p-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#3BB5FF] mb-1.5">
              Reading the dashboard
            </div>
            <ul className="text-[10px] text-[#E0E0E5] leading-relaxed space-y-1 list-disc pl-4">
              <li>Distance BE &gt; 0 ⇒ position currently profitable vs strategy basis.</li>
              <li>Velocity &gt; 0 ⇒ wheel actively collecting (not defending).</li>
              <li>Days → Repair &lt; ~365 ⇒ recovery on a reasonable timeline.</li>
              <li>Velocity &lt; 0 for &gt;14 days on a single position ⇒ consider closing or changing the structure.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function PortfolioAggregateCard({ agg }) {
  const [helpOpen, setHelpOpen] = useState(false);
  if (!agg || (agg.open_positions || 0) === 0) {
    return (
      <>
        <div
          data-testid="perf-wheel-aggregate-card"
          className="bg-[#121216] border border-[#26262B] rounded-sm p-4 mb-4"
        >
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#8A8A93]">
              Wheel ITM · Portfolio Aggregate
            </div>
            <button
              data-testid="wheel-methodology-trigger"
              onClick={() => setHelpOpen(true)}
              className="text-[#5F5F66] hover:text-[#3BB5FF] p-1 rounded-sm hover:bg-[#1A1A20] flex items-center gap-1"
              title="Open methodology drawer: formulas + interpretation"
              aria-label="Open Wheel Risk Snapshot methodology"
            >
              <HelpCircle size={13} />
              <span className="text-[9px] font-mono uppercase tracking-[0.15em] hidden sm:inline">methodology</span>
            </button>
          </div>
          <div className="text-[#5F5F66] text-xs font-mono mt-2">
            No open positions. The wheel is currently premium-only — aggregate metrics will populate once the next CSP gets assigned.
          </div>
        </div>
        <MethodologyDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
      </>
    );
  }
  return (
    <>
    <div
      data-testid="perf-wheel-aggregate-card"
      className="bg-gradient-to-br from-[#121216] to-[#0A0A0D] border border-[#26262B] rounded-sm p-4 mb-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] font-mono font-bold uppercase tracking-[0.18em] text-white">
            Wheel ITM · Portfolio Aggregate
          </div>
          <div className="text-[9px] font-mono text-[#5F5F66] mt-0.5">
            Basis-weighted snapshot across {agg.open_positions} open position{agg.open_positions === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="wheel-methodology-trigger"
            onClick={() => setHelpOpen(true)}
            className="text-[#5F5F66] hover:text-[#3BB5FF] p-1.5 rounded-sm hover:bg-[#1A1A20] flex items-center gap-1.5 border border-transparent hover:border-[#3BB5FF]/40"
            title="Open methodology drawer: formulas + interpretation"
            aria-label="Open Wheel Risk Snapshot methodology"
          >
            <HelpCircle size={13} />
            <span className="text-[9px] font-mono uppercase tracking-[0.15em] hidden sm:inline">methodology</span>
          </button>
          <div className="text-[9px] font-mono text-[#5F5F66] uppercase tracking-[0.15em]">
            since latest cycle reset
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
        <AggregateTile
          label="Open Share Cost"
          value={fmtUsd(agg.total_open_share_cost)}
          tooltip="Σ (accounting basis × shares) across every open position. Total capital locked up in assigned stock."
          testid="agg-open-share-cost"
        />
        {/* Iter119.34 — Audit §3: LOCKED is now the PRIMARY economic-truth tile. */}
        <AggregateTile
          label="Locked Premium Banked"
          value={fmtUsd(agg.portfolio_locked_premium)}
          tone={agg.portfolio_locked_premium > 0 ? "green" : agg.portfolio_locked_premium < 0 ? "red" : "neutral"}
          tooltip="Σ LOCKED option premium = net cashflow EXCLUDING credit still parked on currently-open short options. Primary economic truth — the dollars actually banked from closed/expired legs."
          testid="agg-locked-premium"
        />
        <AggregateTile
          label="Locked Repair %"
          value={fmtPct(agg.portfolio_locked_repair_pct)}
          tone={agg.portfolio_locked_repair_pct > 0 ? "green" : "red"}
          tooltip="Locked Premium Banked ÷ Open Share Cost × 100. Primary capital-recovery rate. Real wound healing."
          testid="agg-locked-repair"
        />
        <AggregateTile
          label="Locked Shortfall"
          value={fmtUsd(agg.portfolio_locked_shortfall)}
          tone="yellow"
          tooltip="Σ (basis − LOCKED premium). Total $ still needed for full break-even using only banked premium. Primary recovery target."
          testid="agg-locked-shortfall"
        />
        <AggregateTile
          label="Open Premium at Stake"
          value={fmtUsd(agg.portfolio_open_premium_at_stake)}
          tone={agg.portfolio_open_premium_at_stake > 0 ? "yellow" : "neutral"}
          tooltip="Σ remaining credit on currently-open short options. NOT YET BANKED — could harden (if expires worthless) or disappear (if BTC'd above credit). Audit doctrine: this is live risk, not income."
          testid="agg-open-at-stake"
        />
        <AggregateTile
          label="Strategy Repair % (incl. open)"
          value={fmtPct(agg.portfolio_strategy_repair_pct ?? agg.weighted_avg_repair_pct)}
          tone={(agg.portfolio_strategy_repair_pct ?? agg.weighted_avg_repair_pct) > 0 ? "green" : "red"}
          tooltip="Secondary state metric. INCLUDES open short credit as if it were already locked. Use locked variant for capital-preservation decisions."
          testid="agg-strategy-repair"
        />
        <AggregateTile
          label="Open Positions"
          value={agg.open_positions}
          tooltip="Number of tickers with assigned shares currently held."
          testid="agg-positions"
        />
      </div>
    </div>
    <MethodologyDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}

function AggregateTile({ label, value, tone = "neutral", tooltip, testid }) {
  const toneCls = {
    green: "text-[#00FF88]",
    red: "text-[#FF3366]",
    yellow: "text-[#FFB800]",
    neutral: "text-white",
  }[tone];
  return (
    <div
      data-testid={testid}
      className="bg-[#0A0A0D] border border-[#26262B] rounded-sm p-2.5 cursor-help"
      title={tooltip}
    >
      <div className="text-[8px] font-mono uppercase tracking-[0.15em] text-[#8A8A93] mb-1 truncate">
        {label}
      </div>
      <div className={`text-base font-mono font-bold ${toneCls}`}>{value ?? "—"}</div>
    </div>
  );
}

// ===========================================================================
// Inventory tab
// ===========================================================================
function InventoryTab({ kpis }) {
  const inv = kpis?.open_inventory || [];
  const agg = kpis?.wheel_aggregate || null;
  // Iter116.5 — Same Copy + CSV pattern for the open-inventory snapshot
  // so the operator can paste a quick "what am I holding right now"
  // summary or export the MTM table for offline analysis.
  const invCsvColumns = [
    { label: "symbol",              get: i => i.symbol },
    { label: "shares",              get: i => i.shares ?? "" },
    { label: "accounting_basis",    get: i => i.accounting_basis ?? "" },
    { label: "effective_basis",     get: i => i.effective_basis ?? "" },
    { label: "conservative_locked_be", get: i => i.conservative_locked_be ?? "" },
    { label: "locked_option_premium", get: i => i.locked_option_premium ?? "" },
    { label: "open_short_credit",     get: i => i.open_short_credit ?? "" },
    { label: "net_option_cashflow", get: i => i.net_option_cashflow ?? "" },
    { label: "cc_open_credit_excluded", get: i => i.cc_open_credit_excluded ?? "" },
    { label: "spot",                get: i => i.spot ?? "" },
    { label: "distance_to_breakeven_pct", get: i => i.distance_to_breakeven_pct ?? "" },
    { label: "unrealised_strategy", get: i => i.unrealised_strategy ?? "" },
    { label: "locked_mtm",          get: i => i.locked_mtm ?? "" },
    { label: "unrealised_accounting", get: i => i.unrealised_accounting ?? "" },
    { label: "days_assigned",       get: i => i.days_assigned ?? "" },
    { label: "days_since_first_cycle_sto", get: i => i.days_since_first_cycle_sto ?? "" },
    { label: "assigned_capital",    get: i => i.assigned_capital ?? "" },
    // Iter119.30/34 — Wheel ITM rollup columns in export.
    { label: "cycle_days",          get: i => i.cycle_days ?? "" },
    { label: "assigned_repair_velocity", get: i => i.assigned_repair_velocity ?? "" },
    { label: "total_cycle_velocity",     get: i => i.total_cycle_velocity ?? "" },
    { label: "locked_repair_pct",   get: i => i.locked_repair_pct ?? "" },
    { label: "strategy_repair_pct", get: i => i.strategy_repair_pct ?? i.repair_pct ?? "" },
    { label: "locked_shortfall",    get: i => i.locked_shortfall ?? "" },
    { label: "strategy_shortfall",  get: i => i.strategy_shortfall ?? "" },
    { label: "annualized_locked_repair_run_rate", get: i => i.annualized_locked_repair_run_rate ?? "" },
    { label: "early_cycle_warning", get: i => i.early_cycle_warning ? "true" : "false" },
    { label: "dte_next_open_short", get: i => i.dte_next_open_short ?? "" },
    { label: "open_premium_at_stake", get: i => i.open_premium_at_stake ?? "" },
    { label: "run_rate_days_to_full_repair", get: i => i.run_rate_days_to_full_repair ?? "" },
  ];
  const buildInvLine = (i) => {
    const parts = [i.symbol || "—", `${i.shares} sh`];
    if (i.effective_basis != null) parts.push(`eff $${i.effective_basis}`);
    if (i.spot != null) parts.push(`spot $${i.spot}`);
    if (i.distance_to_breakeven_pct != null) parts.push(`BE ${fmtPct(i.distance_to_breakeven_pct)}`);
    if (i.unrealised_strategy != null) parts.push(`MTM ${fmtUsd(i.unrealised_strategy)}`);
    if (i.days_assigned != null) parts.push(`${i.days_assigned}d held`);
    return parts.join(" · ");
  };
  return (
    <div>
      <PortfolioAggregateCard agg={agg} />
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-mono text-[#8A8A93]">{inv.length} open positions · quotes from Forge chart cache</div>
        <TableExportToolbar
          rows={inv}
          label="Performance — Inventory Export"
          csvFilename="performance-inventory"
          buildLine={buildInvLine}
          csvColumns={invCsvColumns}
          testidPrefix="perf-inventory"
        />
      </div>
      <div className="bg-[#121216] border border-[#26262B] rounded-sm overflow-auto max-h-[70vh]">
        <table className="w-full text-[11px] font-mono" data-testid="perf-inventory-table">
          <thead>
            <tr className="text-[#8A8A93] border-b border-[#26262B] uppercase tracking-[0.15em] sticky top-0 z-10 bg-[#121216]">
              <th className="text-left px-3 py-2">Symbol</th>
              <th className="text-right px-3 py-2">Shares</th>
              <th className="text-right px-3 py-2">Accounting $</th>
              {/* Iter119.34 — Audit §6: Locked Effective BE is now the PRIMARY break-even. */}
              <th
                className="text-right px-3 py-2 cursor-help"
                title="Locked Effective BE = (open share cost − LOCKED option premium) / shares.&#10;LOCKED premium excludes credit still parked on currently-open short options.&#10;Primary conservative break-even for capital-recovery decisions.&#10;Always >= Effective BE incl. Open Premium."
              >Locked Effective BE</th>
              <th
                className="text-right px-3 py-2 cursor-help"
                title="Effective BE incl. Open Premium = (open share cost − net option cashflow) / shares.&#10;Net option cashflow INCLUDES credit still parked in open short options.&#10;Secondary state metric — assumes open shorts will expire worthless or be closed at zero cost."
              >Effective BE incl. Open</th>
              <th className="text-right px-3 py-2">Spot</th>
              <th className="text-right px-3 py-2">Distance BE</th>
              {/* Iter119.34 — Audit §5.3: MTM Locked Economic is the PRIMARY mark-to-market. */}
              <th
                className="text-right px-3 py-2 cursor-help"
                title="MTM Locked Economic = (spot − Locked Effective BE) × shares.&#10;Primary economic MTM — does NOT count credit still in open shorts."
              >MTM Locked</th>
              <th
                className="text-right px-3 py-2 cursor-help"
                title="MTM Strategy incl. Open = (spot − Effective BE incl. Open) × shares.&#10;Secondary MTM — counts open short credit as if locked."
              >MTM Strategy incl. Open</th>
              <th className="text-right px-3 py-2">MTM Accounting</th>
              <th
                className="text-right px-3 py-2 cursor-help"
                title="Cycle Days = days since the FIRST assignment on the current cycle. Drives velocity / annualisation. Warning: under 7 days, annualised numbers are unstable."
              >Cycle Days</th>
              {/* Iter119.34 — Audit §6: Assigned Repair Velocity uses LOCKED premium since assignment. */}
              <th
                className="text-right px-3 py-2 cursor-help"
                title="Assigned Repair Velocity = LOCKED premium since assignment ÷ cycle days.&#10;Average $/day of CAPITAL RECOVERY (excludes credit still in open shorts).&#10;Audit §5.5 doctrine: only locked premium counts toward repair velocity."
              >Repair Velocity $/d</th>
              {/* Iter119.34 — Audit §5.4: Locked Repair % is the primary recovery metric. */}
              <th
                className="text-right px-3 py-2 cursor-help"
                title="Locked Repair % = LOCKED premium ÷ open share cost × 100.&#10;Primary capital-recovery rate. Real wound healing — excludes open short credit."
              >Locked Repair %</th>
              <th
                className="text-right px-3 py-2 cursor-help"
                title="Strategy Repair % incl. Open = net option cashflow ÷ open share cost × 100.&#10;Secondary state metric — assumes open shorts harden."
              >Strategy Repair % (incl. open)</th>
              {/* Iter119.34 — Audit §6: rename Cycle ROI → Annualised Repair Run-Rate. */}
              <th
                className="text-right px-3 py-2 cursor-help"
                title="Annualised Repair Run-Rate = Locked Repair % × (365 / cycle_days).&#10;Diagnostic, NOT a forecast. Warning badge when cycle_days < 7 — annualisation is unstable on young cycles."
              >Ann. Run-Rate</th>
              {/* Iter119.34 — Audit §5.7: replaced projected_dollars with open_premium_at_stake. */}
              <th
                className="text-right px-3 py-2 cursor-help"
                title="Open Premium Still at Stake = remaining credit on currently-open short options.&#10;NOT YET BANKED — could harden if it expires worthless, or disappear if BTC'd above credit.&#10;Audit doctrine: this is live risk, not projected income."
              >Open Premium at Stake</th>
              <th
                className="text-right px-3 py-2 cursor-help"
                title="Run-Rate Days to Full Repair = Locked shortfall ÷ Assigned Repair Velocity.&#10;Diagnostic projection at CURRENT locked-premium pace. Only defined when velocity is POSITIVE."
              >Days → Repair</th>
              <th className="text-right px-3 py-2">Days Assigned</th>
              <th className="text-right px-3 py-2">Assigned $</th>
            </tr>
          </thead>
          <tbody>
            {inv.length === 0 && (
              <tr><td colSpan={18} className="text-center py-8 text-[#5F5F66]">No open assignments. Wheel is currently premium-only.</td></tr>
            )}
            {inv.map((i, idx) => (
              <tr key={idx} className="border-b border-[#1A1A20] hover:bg-[#0A0A0D]" data-testid={`inv-row-${i.symbol}`}>
                <td className="px-3 py-1.5 text-[#00FF88] font-bold">{i.symbol}</td>
                <td className="px-3 py-1.5 text-right text-white">{i.shares}</td>
                <td className="px-3 py-1.5 text-right text-white">${i.accounting_basis}</td>
                {/* Iter119.34 — LOCKED first (primary). */}
                <td
                  className="px-3 py-1.5 text-right text-[#00FF88] font-bold"
                  data-testid={`inv-locked-be-${i.symbol}`}
                  title={i.open_short_credit > 0
                    ? `Excludes $${i.open_short_credit.toLocaleString()} of credit still on open short options.`
                    : "No open short options — equals Effective BE incl. Open."}
                >${i.locked_effective_be ?? i.conservative_locked_be}</td>
                <td
                  className="px-3 py-1.5 text-right text-[#FFB800]"
                  data-testid={`inv-strategy-be-${i.symbol}`}
                >
                  ${i.effective_basis}
                  {i.open_short_credit > 0 && (
                    <span
                      className="ml-1.5 inline-block bg-[#FFB800]/15 text-[#FFB800] text-[8px] font-bold uppercase tracking-[0.1em] px-1 py-px rounded-sm align-middle"
                      title={`Includes $${i.open_short_credit.toLocaleString()} of OPEN short credit — not yet locked.`}
                    >open</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right text-white">{i.spot ? `$${i.spot}` : "—"}</td>
                <td className={`px-3 py-1.5 text-right ${i.distance_to_breakeven_pct > 0 ? "text-[#00FF88]" : "text-[#FF3366]"}`}>{fmtPct(i.distance_to_breakeven_pct)}</td>
                {/* Iter119.34 — LOCKED MTM first (primary). */}
                <td
                  className={`px-3 py-1.5 text-right font-bold ${i.locked_mtm > 0 ? "text-[#00FF88]" : i.locked_mtm < 0 ? "text-[#FF3366]" : "text-[#5F5F66]"}`}
                  data-testid={`inv-locked-mtm-${i.symbol}`}
                >{fmtUsd(i.locked_mtm)}</td>
                <td className={`px-3 py-1.5 text-right ${i.unrealised_strategy > 0 ? "text-[#00FF88]" : i.unrealised_strategy < 0 ? "text-[#FF3366]" : "text-[#5F5F66]"}`}>{fmtUsd(i.unrealised_strategy)}</td>
                <td className={`px-3 py-1.5 text-right ${i.unrealised_accounting > 0 ? "text-[#00FF88]" : i.unrealised_accounting < 0 ? "text-[#FF3366]" : "text-[#5F5F66]"}`}>{fmtUsd(i.unrealised_accounting)}</td>
                <td className="px-3 py-1.5 text-right text-[#8A8A93]" data-testid={`inv-cycle-days-${i.symbol}`}>
                  {i.cycle_days ?? "—"}
                  {i.early_cycle_warning && (
                    <span
                      className="ml-1 inline-block bg-[#FFB800]/15 text-[#FFB800] text-[8px] font-bold uppercase tracking-[0.1em] px-1 py-px rounded-sm align-middle"
                      title="Cycle under 7 days — annualised numbers (Run-Rate, ROI) are unstable. Do not use as headline performance."
                      data-testid={`inv-early-warning-${i.symbol}`}
                    >early</span>
                  )}
                </td>
                <td
                  className={`px-3 py-1.5 text-right ${i.assigned_repair_velocity > 0 ? "text-[#00FF88]" : i.assigned_repair_velocity < 0 ? "text-[#FF3366]" : "text-[#5F5F66]"}`}
                  data-testid={`inv-velocity-${i.symbol}`}
                  title={i.total_cycle_velocity != null ? `Total cycle velocity (from first STO): $${i.total_cycle_velocity.toFixed(2)}/d` : undefined}
                >
                  {i.assigned_repair_velocity != null ? `$${i.assigned_repair_velocity.toFixed(2)}` : "—"}
                </td>
                <td
                  className={`px-3 py-1.5 text-right font-bold ${i.locked_repair_pct > 0 ? "text-[#00FF88]" : i.locked_repair_pct < 0 ? "text-[#FF3366]" : "text-[#5F5F66]"}`}
                  data-testid={`inv-locked-repair-${i.symbol}`}
                >
                  {fmtPct(i.locked_repair_pct)}
                </td>
                <td
                  className={`px-3 py-1.5 text-right ${(i.strategy_repair_pct ?? i.repair_pct) > 0 ? "text-[#00FF88]" : (i.strategy_repair_pct ?? i.repair_pct) < 0 ? "text-[#FF3366]" : "text-[#5F5F66]"}`}
                  data-testid={`inv-repair-${i.symbol}`}
                >
                  {fmtPct(i.strategy_repair_pct ?? i.repair_pct)}
                </td>
                <td
                  className={`px-3 py-1.5 text-right ${i.annualized_locked_repair_run_rate > 0 ? "text-[#00FF88]" : i.annualized_locked_repair_run_rate < 0 ? "text-[#FF3366]" : "text-[#5F5F66]"}`}
                  data-testid={`inv-run-rate-${i.symbol}`}
                >
                  {fmtPct(i.annualized_locked_repair_run_rate)}
                </td>
                <td
                  className="px-3 py-1.5 text-right text-[#FFB800]"
                  data-testid={`inv-open-at-stake-${i.symbol}`}
                  title={i.dte_next_open_short != null ? `Nearest expiry: ${i.dte_next_open_short} DTE` : "No open shorts"}
                >
                  {fmtUsd(i.open_premium_at_stake)}
                </td>
                <td
                  className="px-3 py-1.5 text-right text-[#FFB800]"
                  data-testid={`inv-days-repair-${i.symbol}`}
                >
                  {i.run_rate_days_to_full_repair != null ? `${i.run_rate_days_to_full_repair}d` : "—"}
                </td>
                <td className="px-3 py-1.5 text-right text-[#8A8A93]">{i.days_assigned ?? "—"}</td>
                <td className="px-3 py-1.5 text-right text-white">${i.assigned_capital}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===========================================================================
// Cycles tab (unchanged from v2)
// ===========================================================================

// Iter120.2 — Cycle Reconstruction Debug Export button.
// Streams the per-transaction audit ledger as CSV from
// GET /api/perf/cycles/debug?format=csv so the operator can reconcile
// every Cycles cell back to a single raw broker fill.
function CycleDebugExportButton({ accountNumber, userParams }) {
  const [busy, setBusy] = useState(false);
  const downloadDebugCsv = async () => {
    setBusy(true);
    try {
      const params = {
        ...(userParams ? userParams() : {}),
        period: "all",
        format: "csv",
        ...(accountNumber ? { account_number: accountNumber } : {}),
      };
      const resp = await axios.get(`${API}/perf/cycles/debug`, {
        params, responseType: "blob", withCredentials: true,
      });
      const today = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cycle-reconstruction-debug-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Cycle debug ledger downloaded");
    } catch (e) {
      toast.error("Failed to download debug ledger");
      // eslint-disable-next-line no-console
      console.error("cycle-debug-export", e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      data-testid="perf-cycles-debug-export"
      onClick={downloadDebugCsv}
      disabled={busy}
      title="Per-transaction debug ledger (Iter120.2) — one row per broker fill with cycle_id, cycle_event, running shares + premium state."
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#FFB800]/30 text-[#FFB800] bg-[#FFB800]/5 rounded-sm text-[10px] font-mono uppercase tracking-[0.15em] hover:bg-[#FFB800]/10 transition-colors disabled:opacity-50"
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Bug size={12} />}
      Debug Export
    </button>
  );
}

// Iter120.5 — Per-cycle Inventory-parity sentinel chip. Shows a tiny
// green check (•) when the open cycle's locked_premium matches the
// matching Inventory row within $1. Goes red with a triangle (⚠) when
// the delta exceeds $1 — surfaces doctrine drift the moment it
// happens (e.g., a future Tastytrade format change that breaks the
// paired-row detector). Closed cycles + cycles without a matching
// Inventory entry render nothing.
function CycleInventoryDeltaChip({ symbol, cycleLocked, cycleStrategy, inv, testidIdx }) {
  if (!inv) return null;
  const invLocked = inv.locked_option_premium;
  if (typeof invLocked !== "number" || typeof cycleLocked !== "number") return null;
  const delta = cycleLocked - invLocked;
  const isAligned = Math.abs(delta) < 1.0;
  const tooltipBase = `Cycle locked $${cycleLocked.toFixed(2)} · Inventory locked $${invLocked.toFixed(2)}`;
  if (isAligned) {
    return (
      <span
        data-testid={`cycle-inv-delta-chip-${symbol}-${testidIdx}`}
        data-aligned="true"
        title={`Inventory parity OK · ${tooltipBase}`}
        className="inline-flex items-center justify-center w-[14px] h-[14px] rounded-full bg-[#00FF88]/15 text-[#00FF88] text-[9px] leading-none cursor-help"
      >
        ✓
      </span>
    );
  }
  return (
    <span
      data-testid={`cycle-inv-delta-chip-${symbol}-${testidIdx}`}
      data-aligned="false"
      title={`Cycle ↔ Inventory drift Δ$${delta.toFixed(2)} · ${tooltipBase}`}
      className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded-sm border border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366] text-[8px] font-mono cursor-help"
    >
      ⚠ Δ${Math.abs(delta).toFixed(0)}
    </span>
  );
}

// Iter121 — Cycles status filter chip. Single button with the
// count baked into the label; active state uses the existing
// accent green, inactive stays neutral so the active selection
// reads at a glance.
function CycleStatusChip({ label, value, active, count, onSelect }) {
  const isActive = active === value;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      data-testid={`perf-cycles-chip-${value}`}
      aria-pressed={isActive}
      className={`text-[10px] font-mono uppercase tracking-[0.12em] px-2.5 py-1 rounded-sm border transition-colors ${
        isActive
          ? "bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/60"
          : "bg-[#0A0A0D] text-[#8A8A93] border-[#26262B] hover:text-white hover:border-[#3A3A40]"
      }`}
    >
      {label}
      <span className={`ml-1.5 ${isActive ? "text-[#00FF88]" : "text-[#5F5F66]"}`}>{count}</span>
    </button>
  );
}



function CyclesTab({ kpis, accountNumber, userParams }) {
  const cycles = kpis?.reconstructed_cycles || [];
  // Iter120.5 — Cycle ↔ Inventory delta sentinel. Index Inventory
  // entries by symbol so every open-cycle row can compare its
  // locked_premium against the matching Inventory row in O(1).
  const inventoryBySymbol = useMemo(() => {
    const m = new Map();
    for (const inv of kpis?.open_inventory || []) {
      if (inv?.symbol) m.set(inv.symbol, inv);
    }
    return m;
  }, [kpis?.open_inventory]);
  // Iter121 — Status filter chips + symbol search. Pure client-side
  // filtering: zero new endpoints, cycles array is already in memory.
  // statusFilter ∈ {"all","open","closed","called_away"} maps to the
  // c.outcome field. "closed" lumps manual_stock_exit + closed_csp
  // since the operator already sees "closed" in the Outcome column.
  const [statusFilter, setStatusFilter] = useState("all");
  const [symbolQuery, setSymbolQuery] = useState("");
  const counts = useMemo(() => {
    const c = { all: cycles.length, open: 0, closed: 0, called_away: 0 };
    for (const cy of cycles) {
      if (cy.outcome === "open") c.open += 1;
      else if (cy.outcome === "called_away") c.called_away += 1;
      else if (cy.outcome) c.closed += 1;
    }
    return c;
  }, [cycles]);
  const filteredCycles = useMemo(() => {
    const q = symbolQuery.trim().toUpperCase();
    return cycles.filter((c) => {
      if (statusFilter === "open" && c.outcome !== "open") return false;
      if (statusFilter === "called_away" && c.outcome !== "called_away") return false;
      if (statusFilter === "closed") {
        if (c.outcome === "open" || c.outcome === "called_away") return false;
      }
      if (q && !(c.symbol || "").toUpperCase().includes(q)) return false;
      return true;
    });
  }, [cycles, statusFilter, symbolQuery]);
  const isFiltered = statusFilter !== "all" || symbolQuery.trim() !== "";
  // Iter116.5 — Closed-cycle P&L is the natural tax / quarterly review
  // surface. Mirror the Journal's Copy + CSV pattern so the operator can
  // one-click ingest into a spreadsheet.
  // Iter119.35 — Cycle ROI math corrected to use cycle_capital_basis as
  // the denominator (was first_strike × 100 — only correct for 1-contract
  // cycles). New fields surface the truth: opening_contract_qty,
  // cycle_capital_basis, closed_cycle_roi_pct (null when open),
  // open_cycle_locked_roi_pct (primary for open cycles),
  // open_cycle_strategy_roi_pct (secondary), locked_premium_return_pct,
  // strategy_premium_return_pct_incl_open, roi_denominator_source.
  const cycleCsvColumns = [
    { label: "symbol",        get: c => c.symbol },
    { label: "origin",        get: c => c.origin || "" },
    { label: "started_at",    get: c => c.started_at || "" },
    { label: "ended_at",      get: c => c.ended_at || "" },
    { label: "days",          get: c => c.days ?? "" },
    { label: "first_strike",  get: c => c.first_strike ?? "" },
    { label: "opening_contract_qty", get: c => c.opening_contract_qty ?? "" },
    { label: "opening_share_qty",    get: c => c.opening_share_qty ?? "" },
    { label: "cycle_capital_basis",  get: c => c.cycle_capital_basis ?? "" },
    { label: "roi_denominator_source", get: c => c.roi_denominator_source || "" },
    { label: "legs_count",    get: c => c.legs_count ?? "" },
    { label: "realized_pnl",  get: c => c.realized_pnl ?? "" },
    // Iter120.4 — Inventory-aligned premium fields. For OPEN cycles
    // these mirror the Inventory pane EXACTLY (operator spec): closed_csp
    // BTC buybacks on the same symbol carry into the open cycle until
    // shares-to-zero triggers an Inventory-style reset.
    { label: "net_option_cashflow", get: c => c.net_option_cashflow ?? "" },
    { label: "locked_premium", get: c => c.locked_premium ?? "" },
    { label: "strategy_option_premium_incl_open", get: c => c.strategy_option_premium_incl_open ?? "" },
    { label: "open_short_credit", get: c => c.open_short_credit ?? "" },
    { label: "closed_cycle_roi_pct",            get: c => c.closed_cycle_roi_pct ?? "" },
    { label: "open_cycle_locked_roi_pct",       get: c => c.open_cycle_locked_roi_pct ?? "" },
    { label: "open_cycle_strategy_roi_pct",     get: c => c.open_cycle_strategy_roi_pct ?? "" },
    { label: "locked_premium_return_pct",       get: c => c.locked_premium_return_pct ?? "" },
    { label: "strategy_premium_return_pct_incl_open", get: c => c.strategy_premium_return_pct_incl_open ?? "" },
    { label: "outcome",       get: c => c.outcome || "" },
  ];
  const buildCycleLine = (c) => {
    const parts = [];
    parts.push(c.symbol || "—");
    parts.push(c.outcome || "—");
    if (c.opening_contract_qty != null) parts.push(`${c.opening_contract_qty}c`);
    if (c.first_strike != null) parts.push(`K $${c.first_strike}`);
    if (c.cycle_capital_basis != null) parts.push(`basis ${fmtUsd(c.cycle_capital_basis)}`);
    if (c.realized_pnl != null) parts.push(`P&L ${fmtUsd(c.realized_pnl)}`);
    const roi = c.outcome === "open" ? c.open_cycle_locked_roi_pct : c.closed_cycle_roi_pct;
    if (roi != null) parts.push(`ROI ${fmtPct(roi)}`);
    if (c.days != null) parts.push(`${c.days}d`);
    if (c.started_at) parts.push(`${c.started_at.slice(0, 10)}→${(c.ended_at || "open").slice(0, 10)}`);
    return parts.join(" · ");
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="text-[11px] font-mono text-[#8A8A93]">
          {isFiltered
            ? <span data-testid="perf-cycles-filter-summary">{filteredCycles.length} of {cycles.length} cycles · filter active</span>
            : <span data-testid="perf-cycles-filter-summary">{cycles.length} cycles reconstructed · Iter119.35 basis ROI · Iter120.3 pair-order · Iter120.4 Inventory-aligned premium</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <CycleDebugExportButton accountNumber={accountNumber} userParams={userParams} />
          <TableExportToolbar
            rows={filteredCycles}
            label="Performance — Cycles Export"
            csvFilename="performance-cycles"
            buildLine={buildCycleLine}
            csvColumns={cycleCsvColumns}
            testidPrefix="perf-cycles"
          />
        </div>
      </div>
      {/* Iter121 — Status filter chips + symbol search. */}
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap" data-testid="perf-cycles-filterbar">
        <div className="flex items-center gap-1.5 flex-wrap">
          <CycleStatusChip label="All"          value="all"          active={statusFilter} count={counts.all}         onSelect={setStatusFilter} />
          <CycleStatusChip label="Open"         value="open"         active={statusFilter} count={counts.open}        onSelect={setStatusFilter} />
          <CycleStatusChip label="Closed"       value="closed"       active={statusFilter} count={counts.closed}      onSelect={setStatusFilter} />
          <CycleStatusChip label="Called Away"  value="called_away"  active={statusFilter} count={counts.called_away} onSelect={setStatusFilter} />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={symbolQuery}
            onChange={(e) => setSymbolQuery(e.target.value)}
            placeholder="Search symbol…"
            className="bg-[#0A0A0D] border border-[#26262B] rounded-sm px-2.5 py-1 text-[11px] font-mono text-white w-40 focus:outline-none focus:border-[#00FF88] placeholder-[#5F5F66]"
            data-testid="perf-cycles-symbol-search"
            aria-label="Search cycles by symbol"
          />
          {symbolQuery && (
            <button
              type="button"
              onClick={() => setSymbolQuery("")}
              className="text-[10px] font-mono text-[#8A8A93] hover:text-white uppercase tracking-[0.1em]"
              data-testid="perf-cycles-symbol-clear"
            >Clear</button>
          )}
        </div>
      </div>
      <div className="bg-[#121216] border border-[#26262B] rounded-sm overflow-auto max-h-[70vh]">
        <table className="w-full text-[11px] font-mono" data-testid="perf-cycles-table">
          <thead>
            <tr className="text-[#8A8A93] border-b border-[#26262B] uppercase tracking-[0.15em] sticky top-0 z-10 bg-[#121216]">
              <th className="text-left px-3 py-2">Symbol</th>
              <th className="text-left px-3 py-2">Started</th>
              <th className="text-left px-3 py-2">Ended</th>
              <th className="text-right px-3 py-2">Days</th>
              <th className="text-right px-3 py-2">First K</th>
              <th
                className="text-right px-3 py-2 cursor-help"
                title="Opening contract quantity (audit §5). Number of CSP contracts that ORIGINATED this cycle. Drives capital_basis when no assignment has happened yet."
              >Qty</th>
              <th
                className="text-right px-3 py-2 cursor-help"
                title="Cycle Capital Basis = actual capital committed to this cycle.&#10;CSP-no-assignment: Σ(strike × contracts × 100)&#10;CSP-assigned: Σ(assignment_price × shares)&#10;Stock origin: Σ(buy_price × shares)&#10;Multi-lot: Σ per-lot.&#10;The denominator for every ROI metric below."
              >Cycle Basis</th>
              <th className="text-right px-3 py-2">Legs</th>
              <th className="text-right px-3 py-2">P&L</th>
              <th
                className="text-right px-3 py-2 cursor-help"
                title="For CLOSED cycles: realized_pnl ÷ cycle_capital_basis × 100. For OPEN cycles: shows the Open Locked ROI % (realized + locked unrealised stock MTM)."
              >Cycle ROI %</th>
              <th
                className="text-right px-3 py-2 cursor-help"
                title="Open-cycle ONLY: (realized + STRATEGY MTM incl. open credit) ÷ basis × 100. Secondary state view."
              >Open Strategy ROI %</th>
              <th
                className="text-right px-3 py-2 cursor-help"
                title="Locked Premium Return % = locked_premium ÷ basis × 100. Not the cycle ROI — just the option-premium yield component. Use for premium quality assessment."
              >Locked Prem %</th>
              <th className="text-left px-3 py-2">Outcome</th>
            </tr>
          </thead>
          <tbody>
            {cycles.length === 0 && (
              <tr><td colSpan={12} className="text-center py-8 text-[#5F5F66]">No cycles reconstructed — import a broker file first.</td></tr>
            )}
            {cycles.length > 0 && filteredCycles.length === 0 && (
              <tr><td colSpan={12} className="text-center py-8 text-[#5F5F66]" data-testid="perf-cycles-empty-filtered">No cycles match the current filter.</td></tr>
            )}
            {filteredCycles.map((c, i) => {
              const primary_roi = c.outcome === "open" ? c.open_cycle_locked_roi_pct : c.closed_cycle_roi_pct;
              const denom_label = {
                CSP_NOTIONAL: "CSP notional",
                ASSIGNED_SHARE_COST: "Assigned shares",
                STOCK_BUY_COST: "Buy-write cost",
                MULTI_LOT_COST: "Multi-lot cost",
              }[c.roi_denominator_source] || c.roi_denominator_source || "—";
              return (
              <tr key={i} className="border-b border-[#1A1A20] hover:bg-[#0A0A0D]" data-testid={`cycle-row-${c.symbol}-${i}`}>
                <td className="px-3 py-1.5">
                  <span className="text-[#00FF88] font-bold">{c.symbol}</span>
                  {c.origin && (
                    <span className="ml-1.5 text-[8px] font-mono text-[#5F5F66] uppercase">{c.origin}</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-white">{c.started_at?.slice(0, 10)}</td>
                <td className="px-3 py-1.5 text-white">{c.ended_at?.slice(0, 10) || "—"}</td>
                <td className="px-3 py-1.5 text-right text-[#8A8A93]">{c.days ?? "—"}</td>
                <td className="px-3 py-1.5 text-right text-white">${c.first_strike}</td>
                <td className="px-3 py-1.5 text-right text-[#8A8A93]" data-testid={`cycle-qty-${c.symbol}-${i}`}>
                  {c.opening_contract_qty ?? "—"}
                </td>
                <td
                  className="px-3 py-1.5 text-right text-white font-bold"
                  data-testid={`cycle-basis-${c.symbol}-${i}`}
                  title={`Denominator source: ${denom_label}`}
                >
                  {fmtUsd(c.cycle_capital_basis)}
                </td>
                <td className="px-3 py-1.5 text-right text-[#8A8A93]">{c.legs_count}</td>
                <td className={`px-3 py-1.5 text-right font-bold ${c.realized_pnl > 0 ? "text-[#00FF88]" : c.realized_pnl < 0 ? "text-[#FF3366]" : "text-[#5F5F66]"}`}>{fmtUsd(c.realized_pnl)}</td>
                <td
                  className={`px-3 py-1.5 text-right font-bold ${primary_roi > 0 ? "text-[#00FF88]" : primary_roi < 0 ? "text-[#FF3366]" : "text-[#5F5F66]"}`}
                  data-testid={`cycle-roi-${c.symbol}-${i}`}
                  title={c.outcome === "open" ? "Open Cycle Locked ROI — uses locked premium + locked-MTM only" : "Closed Cycle ROI — final realized P&L ÷ capital basis"}
                >
                  {fmtPct(primary_roi)}
                </td>
                <td
                  className={`px-3 py-1.5 text-right ${c.open_cycle_strategy_roi_pct > 0 ? "text-[#00FF88]" : c.open_cycle_strategy_roi_pct < 0 ? "text-[#FF3366]" : "text-[#5F5F66]"}`}
                  data-testid={`cycle-strategy-roi-${c.symbol}-${i}`}
                >
                  {c.outcome === "open" ? fmtPct(c.open_cycle_strategy_roi_pct) : "—"}
                </td>
                <td
                  className={`px-3 py-1.5 text-right ${c.locked_premium_return_pct > 0 ? "text-[#00FF88]" : c.locked_premium_return_pct < 0 ? "text-[#FF3366]" : "text-[#5F5F66]"}`}
                  data-testid={`cycle-locked-prem-${c.symbol}-${i}`}
                >
                  <div className="flex items-center justify-end gap-1.5">
                    {fmtPct(c.locked_premium_return_pct)}
                    {c.outcome === "open" && (
                      <CycleInventoryDeltaChip
                        symbol={c.symbol}
                        cycleLocked={c.locked_premium}
                        cycleStrategy={c.strategy_option_premium_incl_open}
                        inv={inventoryBySymbol.get(c.symbol)}
                        testidIdx={i}
                      />
                    )}
                  </div>
                </td>
                <td className={`px-3 py-1.5 text-[10px] uppercase ${c.outcome === "open" ? "text-[#FFB800]" : c.outcome === "called_away" ? "text-[#3BB5FF]" : "text-[#8A8A93]"}`}>{c.outcome}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===========================================================================
// Shared helpers
// ===========================================================================

// ──────────────────────────────────────────────────────────────────────
// Iter120.6 — Monthly P&L sign-aware coloring + theme picker.
// Operator request: distinguish negative months at a glance (positive
// = green, negative = red) AND let the operator pick the palette. The
// chart is the operator's primary monthly performance scoreboard;
// surfacing the sign instantly cuts the time-to-insight on losing
// months from a tooltip-hover to a glance.
//
// Each `<Bar>` (`wheel_pnl`, `nonwheel_pnl`) iterates its data via
// `<Cell>` and picks fill from `theme.wheelPos / wheelNeg / otherPos /
// otherNeg` based on the segment's sign. Recharts stacks signed bars
// above / below the zero line automatically.
// ──────────────────────────────────────────────────────────────────────
const MONTHLY_PNL_THEMES = {
  default:    { name: "Default",     wheelPos: "#00FF88", wheelNeg: "#FF3366", otherPos: "#3BB5FF", otherNeg: "#FFB800" },
  colorblind: { name: "Colorblind",  wheelPos: "#56B4E9", wheelNeg: "#E69F00", otherPos: "#009E73", otherNeg: "#CC79A7" },
  mono:       { name: "Monochrome",  wheelPos: "#FFFFFF", wheelNeg: "#6E6E76", otherPos: "#C0C0C8", otherNeg: "#3A3A40" },
};

function useMonthlyPnlTheme() {
  const KEY = "arxmentis:monthlyPnlTheme";
  const [name, setName] = useState(() => {
    try {
      const v = window.localStorage.getItem(KEY);
      return v && MONTHLY_PNL_THEMES[v] ? v : "default";
    } catch (_e) {
      return "default";
    }
  });
  const [custom, setCustom] = useState(() => {
    try {
      const raw = window.localStorage.getItem(KEY + ":custom");
      return raw ? JSON.parse(raw) : { ...MONTHLY_PNL_THEMES.default, name: "Custom" };
    } catch (_e) {
      return { ...MONTHLY_PNL_THEMES.default, name: "Custom" };
    }
  });
  const select = (next) => {
    setName(next);
    try { window.localStorage.setItem(KEY, next); } catch (_e) { /* ignore */ }
  };
  const updateCustom = (key, value) => {
    const next = { ...custom, [key]: value };
    setCustom(next);
    try { window.localStorage.setItem(KEY + ":custom", JSON.stringify(next)); } catch (_e) { /* ignore */ }
  };
  const palette = name === "custom" ? custom : MONTHLY_PNL_THEMES[name];
  return { name, palette, select, custom, updateCustom };
}

function MonthlyPnlThemePicker() {
  const { name, palette, select, custom, updateCustom } = useMonthlyPnlTheme();
  const [showCustom, setShowCustom] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#5F5F66]">Palette</span>
      <select
        data-testid="monthly-pnl-theme-select"
        value={name}
        onChange={(e) => {
          const v = e.target.value;
          select(v);
          setShowCustom(v === "custom");
        }}
        className="bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1 text-[10px] font-mono text-white focus:border-[#00FF88] focus:outline-none"
      >
        {Object.entries(MONTHLY_PNL_THEMES).map(([k, v]) => (
          <option key={k} value={k}>{v.name}</option>
        ))}
        <option value="custom">Custom</option>
      </select>
      <div className="flex items-center gap-1" data-testid="monthly-pnl-theme-swatches">
        <span className="w-2.5 h-2.5 rounded-sm border border-[#26262B]" style={{ background: palette.wheelPos }} title="Premium+Stock (positive)" />
        <span className="w-2.5 h-2.5 rounded-sm border border-[#26262B]" style={{ background: palette.wheelNeg }} title="Premium+Stock (negative)" />
        <span className="w-2.5 h-2.5 rounded-sm border border-[#26262B]" style={{ background: palette.otherPos }} title="Other (positive)" />
        <span className="w-2.5 h-2.5 rounded-sm border border-[#26262B]" style={{ background: palette.otherNeg }} title="Other (negative)" />
      </div>
      {name === "custom" && (
        <button
          type="button"
          data-testid="monthly-pnl-theme-edit"
          onClick={() => setShowCustom(s => !s)}
          className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#FFB800] hover:text-[#00FF88]"
        >
          {showCustom ? "Done" : "Edit"}
        </button>
      )}
      {showCustom && name === "custom" && (
        <div className="flex items-center gap-2 ml-2" data-testid="monthly-pnl-theme-custom">
          {[
            ["wheelPos", "Wheel ↑"],
            ["wheelNeg", "Wheel ↓"],
            ["otherPos", "Other ↑"],
            ["otherNeg", "Other ↓"],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-1 text-[9px] font-mono text-[#8A8A93]">
              {label}
              <input
                type="color"
                value={custom[key]}
                onChange={(e) => updateCustom(key, e.target.value)}
                className="w-5 h-5 bg-transparent border border-[#26262B] rounded-sm cursor-pointer"
                data-testid={`monthly-pnl-theme-${key}`}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function MonthlyPnlChart({ data }) {
  const { palette } = useMonthlyPnlTheme();
  return (
    <div data-testid="perf-monthly-chart" style={{ width: "100%", minWidth: 320, height: 260, minHeight: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 5, right: 24, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="#1A1A20" strokeDasharray="2 4" />
          <XAxis dataKey="month" stroke="#5F5F66" style={{ fontSize: "10px", fontFamily: "monospace" }} />
          <YAxis stroke="#5F5F66" style={{ fontSize: "10px", fontFamily: "monospace" }} tickFormatter={v => fmtUsd(v, 0)} />
          <RTooltip contentStyle={{ background: "#08080A", border: "1px solid #26262B", fontFamily: "monospace", fontSize: "11px" }}
            labelStyle={{ color: "#8A8A93" }} formatter={v => fmtUsd(v)} />
          <Bar dataKey="wheel_pnl" name="Premium+Stock" stackId="a">
            {data.map((d, i) => (
              <Cell key={`wheel-${i}`} fill={(d.wheel_pnl || 0) >= 0 ? palette.wheelPos : palette.wheelNeg} />
            ))}
          </Bar>
          <Bar dataKey="nonwheel_pnl" name="Other" stackId="a">
            {data.map((d, i) => (
              <Cell key={`other-${i}`} fill={(d.nonwheel_pnl || 0) >= 0 ? palette.otherPos : palette.otherNeg} />
            ))}
          </Bar>
          <Legend wrapperStyle={{ fontSize: "10px", fontFamily: "monospace", color: "#8A8A93" }} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Section({ title, children, extras = null }) {
  return (
    <div className="bg-[#121216] border border-[#26262B] rounded-sm p-4">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="text-[11px] font-mono font-bold uppercase tracking-[0.15em] text-[#8A8A93]">{title}</div>
        {extras}
      </div>
      {children}
    </div>
  );
}
function SectionHeader({ title, subtitle }) {
  return (
    <div className="pt-2">
      <div className="text-[11px] font-mono font-bold uppercase tracking-[0.18em] text-white">{title}</div>
      <div className="text-[10px] font-mono text-[#5F5F66]">{subtitle}</div>
    </div>
  );
}
function Empty({ children }) {
  return <div className="text-center py-10 text-[#5F5F66] text-xs font-mono">{children}</div>;
}
function KpiCard({ label, value, icon: Icon, tone = "neutral", hint, testid, tooltip }) {
  const toneCls = { green: "text-[#00FF88]", red: "text-[#FF3366]", yellow: "text-[#FFB800]", neutral: "text-white" }[tone];
  return (
    <div
      data-testid={testid}
      className={`bg-[#121216] border border-[#26262B] rounded-sm p-3${tooltip ? " cursor-help" : ""}`}
      title={tooltip || undefined}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#8A8A93]">{label}</div>
        {Icon && <Icon size={12} className="text-[#5F5F66]" />}
      </div>
      <div className={`text-2xl font-mono font-bold ${toneCls}`}>{value ?? "—"}</div>
      {hint && <div className="text-[9px] font-mono text-[#5F5F66] mt-1">{hint}</div>}
    </div>
  );
}
function toneByValue(v) {
  if (v == null) return "neutral";
  if (v > 0) return "green";
  if (v < 0) return "red";
  return "neutral";
}
function fmtUsd(v, dp = 2) {
  if (v == null || Number.isNaN(v)) return "—";
  const abs = Math.abs(v);
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(dp === 0 ? 0 : 2)}K`;
  return `$${Number(v).toFixed(dp)}`;
}
function fmtPct(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `${Number(v).toFixed(2)}%`;
}

// ===========================================================================
// Import tab
// ===========================================================================
function ImportTab({ refreshKpis, userParams, accounts }) {
  const fileRef = useRef(null);
  const [source, setSource] = useState("auto");
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [batches, setBatches] = useState([]);
  const [error, setError] = useState(null);
  const [committedToast, setCommittedToast] = useState(null);
  const [tastyAccounts, setTastyAccounts] = useState([]);
  const [tastyConnected, setTastyConnected] = useState(false);
  const [tastySelected, setTastySelected] = useState("");
  const [tastySince, setTastySince] = useState("");
  const [syncing, setSyncing] = useState(false);
  // Iter119.31 — When connections exist but ALL fail (broken refresh
  // tokens / OAuth lapses), the green "Sync from Tastytrade" card used
  // to hide silently, making the feature look removed. We now surface
  // a clear actionable error card with a link to re-auth.
  const [tastyConnDiag, setTastyConnDiag] = useState({
    has_connections: false,
    broken: [],
    healthy_count: 0,
  });
  // Iter119.24 — Sync All progress + result. Null = idle. Object during loop.
  const [syncAllProgress, setSyncAllProgress] = useState(null);
  const [syncAllResults, setSyncAllResults] = useState(null);
  // Iter119.19 — Optional CSV-upload account tagger. When set, every row in
  // the staged batch inherits this account_number on commit, so legacy CSVs
  // partition cleanly into the right sub-account.
  const [uploadAccount, setUploadAccount] = useState("");

  const loadBatches = async () => {
    try { const r = await axios.get(`${API}/perf/batches`, { params: userParams() }); setBatches(r.data); }
    catch { /* ignore */ }
  };
  const loadTastyAccounts = async () => {
    try {
      // Iter119.22 — Aggregate sub-accounts across EVERY healthy Tastytrade
      // connection the user owns, not just the first. Each option carries
      // its `connection_id` + nickname so the sync POST routes through the
      // right broker login.
      let conns = [];
      try {
        const cr = await axios.get(`${API}/connections`);
        conns = (cr.data || []).filter(c => c.broker === "tastytrade");
      } catch { conns = []; }

      if (conns.length === 0) {
        // No per-user connections at all → fall back to the legacy
        // (admin-global) path so admins keep working during migration.
        const r = await axios.get(`${API}/perf/sync/tastytrade/accounts`);
        const accts = (r.data?.accounts || []).map(a => ({
          ...a,
          connection_id: r.data?.connection_id || null,
          connection_nickname: null,
        }));
        setTastyConnected(!!r.data?.connected);
        setTastyAccounts(accts);
        // Iter119.31 — Diagnostic: no per-user connections AND the legacy
        // global path failed → surface as a "no connections" prompt.
        setTastyConnDiag({
          has_connections: false,
          broken: [],
          healthy_count: r.data?.connected ? 1 : 0,
          legacy_error: r.data?.connected ? null : (r.data?.detail || null),
        });
        if (accts.length && !tastySelected) setTastySelected(accts[0].account_number);
        return;
      }

      const allAccts = [];
      let anyConnected = false;
      // Iter119.31 — Track which connections failed so the UI can render
      // a useful "go fix it" error card instead of silently hiding sync.
      const brokenConns = [];
      let healthyCount = 0;
      // Use Promise.allSettled so one broken connection doesn't drop the
      // healthy ones from the picker.
      const results = await Promise.allSettled(conns.map(c =>
        axios.get(`${API}/perf/sync/tastytrade/accounts`, { params: { connection_id: c.id } })
          .then(r => ({ conn: c, data: r.data }))
      ));
      for (const res of results) {
        if (res.status !== "fulfilled") continue;
        const { conn, data } = res.value;
        if (data?.connected) {
          anyConnected = true;
          healthyCount += 1;
        } else {
          brokenConns.push({
            id: conn.id,
            nickname: conn.nickname || conn.broker,
            detail: data?.detail || conn.last_error || "Connection failed",
          });
        }
        for (const a of (data?.accounts || [])) {
          allAccts.push({
            ...a,
            connection_id: conn.id,
            connection_nickname: conn.nickname || conn.broker,
          });
        }
      }
      setTastyConnected(anyConnected);
      setTastyAccounts(allAccts);
      setTastyConnDiag({
        has_connections: true,
        broken: brokenConns,
        healthy_count: healthyCount,
      });
      if (allAccts.length && !tastySelected) {
        // Compound select key so users with the same account_number on two
        // brokers (rare but possible) can still distinguish in the dropdown.
        setTastySelected(`${allAccts[0].connection_id || "_"}::${allAccts[0].account_number}`);
      }
    } catch {
      setTastyConnected(false);
      setTastyConnDiag({ has_connections: false, broken: [], healthy_count: 0 });
    }
  };
  useEffect(() => { loadBatches(); loadTastyAccounts(); /* eslint-disable-next-line */ }, []);

  const doUpload = async (file) => {
    setError(null); setUploading(true); setPreview(null); setCommittedToast(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("source", source);
    // Iter119.19 — tag this batch with the chosen account_number (optional).
    if (uploadAccount) fd.append("account_number", uploadAccount);
    try { const r = await axios.post(`${API}/perf/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setPreview(r.data); }
    catch (e) { setError(e?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };
  const doTastySync = async () => {
    if (!tastySelected) return;
    setError(null); setSyncing(true); setPreview(null); setCommittedToast(null);
    try {
      // Iter119.22 — `tastySelected` is now a compound key
      // `${connection_id}::${account_number}` so the sync POST routes
      // through the chosen broker login.
      const sep = tastySelected.indexOf("::");
      const connId = sep >= 0 ? tastySelected.slice(0, sep) : "";
      const acct = sep >= 0 ? tastySelected.slice(sep + 2) : tastySelected;
      const params = { account_number: acct };
      if (connId && connId !== "_") params.connection_id = connId;
      if (tastySince) params.start_date = tastySince;
      const r = await axios.post(`${API}/perf/sync/tastytrade`, null, { params });
      setPreview(r.data);
    } catch (e) { setError(e?.response?.data?.detail || "Sync failed"); }
    setSyncing(false);
  };

  // Iter119.24 — One-click sync across EVERY sub-account from EVERY healthy
  // Tastytrade connection. Auto-commits each batch in series so the operator
  // doesn't need to click Commit per account. Progress is surfaced inline
  // (current account label + N/M counter) so a 6-account sync stays
  // transparent rather than feeling stuck on a spinner.
  const doSyncAll = async () => {
    if (!tastyAccounts.length) return;
    if (!window.confirm(`Sync ALL ${tastyAccounts.length} sub-account(s) across every connection and auto-commit each batch? This may take ~5s per account.`)) return;
    setError(null);
    setSyncing(true);
    setPreview(null);
    setCommittedToast(null);
    setSyncAllResults(null);
    const total = tastyAccounts.length;
    const results = [];
    let totalCommitted = 0;
    let totalSkipped = 0;
    let totalFailed = 0;
    for (let i = 0; i < total; i++) {
      const a = tastyAccounts[i];
      setSyncAllProgress({
        current: i + 1,
        total,
        label: `${a.account_number} · ${a.nickname || ""}${a.connection_nickname ? ` — via ${a.connection_nickname}` : ""}`,
      });
      try {
        const params = { account_number: a.account_number };
        if (a.connection_id) params.connection_id = a.connection_id;
        if (tastySince) params.start_date = tastySince;
        // 1) Stage preview batch.
        const stage = await axios.post(`${API}/perf/sync/tastytrade`, null, { params });
        const batchId = stage.data?.id;
        if (!batchId) throw new Error("No batch id returned from staging");
        // 2) Auto-commit it.
        const commit = await axios.post(`${API}/perf/commit/${batchId}`);
        const committed = commit.data?.committed_rows || 0;
        const skipped = commit.data?.skipped_duplicates || 0;
        totalCommitted += committed;
        totalSkipped += skipped;
        results.push({
          account_number: a.account_number,
          account_label: a.nickname || a.type || "",
          connection_nickname: a.connection_nickname || "",
          status: "ok",
          committed,
          skipped,
        });
      } catch (e) {
        totalFailed += 1;
        results.push({
          account_number: a.account_number,
          account_label: a.nickname || a.type || "",
          connection_nickname: a.connection_nickname || "",
          status: "error",
          error: e?.response?.data?.detail || e?.message || "Sync failed",
        });
      }
    }
    setSyncAllProgress(null);
    setSyncing(false);
    setSyncAllResults({
      total,
      committed: totalCommitted,
      skipped: totalSkipped,
      failed: totalFailed,
      results,
    });
    await loadBatches();
    refreshKpis();
    if (totalFailed === 0) {
      toast.success(`Sync All complete · ${totalCommitted} committed · ${totalSkipped} duplicates skipped across ${total} account(s)`);
    } else {
      toast.error(`Sync All finished with ${totalFailed}/${total} failure(s) — see the result panel below`);
    }
  };
  const commit = async () => {
    if (!preview?.id) return;
    setUploading(true);
    try {
      const r = await axios.post(`${API}/perf/commit/${preview.id}`);
      setCommittedToast({ committed: r.data.committed_rows, skipped: r.data.skipped_duplicates });
      setPreview(null); await loadBatches(); refreshKpis();
    } catch (e) { setError(e?.response?.data?.detail || "Commit failed"); }
    setUploading(false);
  };
  const rollback = async (batchId) => {
    if (!window.confirm("Delete this batch and all imported rows?")) return;
    try {
      await axios.delete(`${API}/perf/batches/${batchId}`);
      await loadBatches();
      refreshKpis();
      toast.success("Batch deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-5">
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-4">
        {/* Iter119.31 — Broken-connection error card. Renders when the
            user HAS Tastytrade connection rows but NONE of them are
            currently healthy (refresh token expired / OAuth revoked).
            Tells the operator exactly why Sync is unavailable and gives
            a one-click reauth path. */}
        {tastyConnDiag.has_connections && tastyConnDiag.healthy_count === 0 && tastyConnDiag.broken.length > 0 && (
          <div
            data-testid="perf-tasty-broken-card"
            className="bg-gradient-to-br from-[#FF3366]/5 to-[#121216] border border-[#FF3366]/40 rounded-sm p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className="text-[#FF3366]" />
              <div className="text-xs font-mono font-bold uppercase tracking-[0.15em] text-[#FF3366]">
                Tastytrade sync unavailable · {tastyConnDiag.broken.length} broken connection{tastyConnDiag.broken.length === 1 ? "" : "s"}
              </div>
            </div>
            <div className="text-[11px] font-mono text-[#E0E0E5] mb-3 leading-relaxed">
              Your Tastytrade refresh tokens have expired or been revoked. Sync
              from broker is currently disabled. Re-authorise each connection
              below to restore the green <span className="text-[#00FF88]">SYNC NOW / SYNC ALL</span> buttons.
            </div>
            <div className="space-y-2">
              {tastyConnDiag.broken.map(b => (
                <div
                  key={b.id}
                  data-testid={`perf-tasty-broken-row-${b.id}`}
                  className="bg-[#0A0A0E] border border-[#26262B] rounded-sm p-2.5 flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-mono font-bold text-white">{b.nickname}</div>
                    <div className="text-[9px] font-mono text-[#FF3366] truncate" title={b.detail}>
                      {b.detail}
                    </div>
                  </div>
                  <button
                    data-testid={`perf-tasty-reauth-${b.id}`}
                    onClick={async () => {
                      try {
                        // Iter119.33 — pass our origin explicitly (see SettingsPage.js).
                        const redirectUri = `${window.location.origin}/api/oauth/tastytrade/callback`;
                        const r = await axios.get(`${API}/oauth/tastytrade/authorize`, {
                          params: { connection_id: b.id, redirect_uri: redirectUri },
                        });
                        if (r.data?.url) {
                          window.location.href = r.data.url;
                        } else {
                          toast.error("Could not start OAuth flow");
                        }
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "OAuth init failed");
                      }
                    }}
                    className="bg-[#3BB5FF] text-[#08080A] text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm hover:bg-[#5FC4FF] flex items-center gap-1.5"
                  >
                    <Link2 size={11} /> Re-authorise
                  </button>
                </div>
              ))}
            </div>
            <div className="text-[10px] font-mono text-[#8A8A93] mt-3 border-t border-[#26262B] pt-2">
              Tip: You can also manage connections from <span className="text-white">Performance → Settings → Connected Brokers</span>. Until the OAuth is refreshed, the CSV upload + Reconcile tools below remain available as a manual fallback.
            </div>
          </div>
        )}
        {/* Iter119.31 — "No Tastytrade connections" prompt. Shown when the
            user has zero Tastytrade rows AND the legacy global path also
            isn't connected. */}
        {!tastyConnDiag.has_connections && !tastyConnected && (
          <div
            data-testid="perf-tasty-noconn-card"
            className="bg-gradient-to-br from-[#3BB5FF]/5 to-[#121216] border border-[#3BB5FF]/30 rounded-sm p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Link2 size={14} className="text-[#3BB5FF]" />
              <div className="text-xs font-mono font-bold uppercase tracking-[0.15em] text-[#3BB5FF]">
                Connect Tastytrade to enable direct sync
              </div>
            </div>
            <div className="text-[11px] font-mono text-[#E0E0E5] leading-relaxed">
              Add a Tastytrade connection in <span className="text-white">Performance → Settings → Connected Brokers</span>, complete the one-click OAuth, and the green <span className="text-[#00FF88]">SYNC NOW / SYNC ALL</span> buttons will appear here.
            </div>
            {tastyConnDiag.legacy_error && (
              <div className="text-[9px] font-mono text-[#FF3366] mt-2 truncate" title={tastyConnDiag.legacy_error}>
                Legacy connection error: {tastyConnDiag.legacy_error}
              </div>
            )}
          </div>
        )}
        {tastyConnected && tastyAccounts.length > 0 && (
          <div data-testid="perf-tasty-sync-card" className="bg-gradient-to-br from-[#00FF88]/5 to-[#121216] border border-[#00FF88]/30 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Zap size={16} className="text-[#00FF88]" />
              <div className="text-xs font-mono font-bold uppercase tracking-[0.15em] text-[#00FF88]">Sync from Tastytrade</div>
              <span className="text-[10px] font-mono text-[#5F5F66] ml-auto">direct API · no CSV needed</span>
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#8A8A93] mb-1">Account</div>
                <select data-testid="perf-tasty-account" value={tastySelected} onChange={e => setTastySelected(e.target.value)}
                  className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono">
                  {tastyAccounts.map(a => {
                    const key = `${a.connection_id || "_"}::${a.account_number}`;
                    const label = a.connection_nickname
                      ? `${a.account_number} · ${a.nickname || a.type || "account"} — via ${a.connection_nickname}`
                      : `${a.account_number} · ${a.nickname || a.type || "account"}`;
                    return (<option key={key} value={key}>{label}</option>);
                  })}
                </select>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#8A8A93] mb-1">From (optional)</div>
                <input data-testid="perf-tasty-since" type="date" value={tastySince} onChange={e => setTastySince(e.target.value)}
                  className="bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono" />
              </div>
              <button data-testid="perf-tasty-sync-btn" onClick={doTastySync} disabled={syncing || !tastySelected}
                className="bg-[#00FF88] text-[#08080A] text-xs font-bold uppercase tracking-[0.15em] px-4 py-2 rounded-sm hover:bg-[#33FF9F] disabled:opacity-40 flex items-center gap-2">
                {syncing ? <><Loader2 size={12} className="animate-spin" /> Syncing…</> : <><Zap size={12} /> Sync Now</>}
              </button>
              <button data-testid="perf-tasty-sync-all-btn" onClick={doSyncAll} disabled={syncing || tastyAccounts.length === 0}
                title={tastyAccounts.length ? `Sync ALL ${tastyAccounts.length} sub-account(s) and auto-commit each batch` : "No sub-accounts available"}
                className="bg-transparent border border-[#00FF88]/60 text-[#00FF88] text-xs font-bold uppercase tracking-[0.15em] px-4 py-2 rounded-sm hover:bg-[#00FF88]/10 disabled:opacity-40 flex items-center gap-2">
                <Repeat size={12} /> Sync All <span className="bg-[#00FF88]/15 px-1.5 py-0.5 rounded-sm text-[9px]">{tastyAccounts.length}</span>
              </button>
            </div>
            <div className="text-[10px] font-mono text-[#5F5F66] mt-2">Leave "From" empty to incrementally sync since the last imported transaction (minus 7 days safety window).</div>
            {syncAllProgress && (
              <div data-testid="perf-sync-all-progress" className="mt-3 bg-[#0A0A0E] border border-[#00FF88]/40 rounded-sm px-3 py-2 flex items-center gap-3">
                <Loader2 size={12} className="animate-spin text-[#00FF88]" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono text-white truncate">
                    Syncing <span className="text-[#00FF88]">{syncAllProgress.label}</span>
                  </div>
                  <div className="text-[9px] font-mono text-[#5F5F66] mt-0.5">
                    Account {syncAllProgress.current} of {syncAllProgress.total} · auto-committing each batch…
                  </div>
                </div>
                <div className="text-[10px] font-mono text-[#00FF88] font-bold tabular-nums">
                  {syncAllProgress.current}/{syncAllProgress.total}
                </div>
              </div>
            )}
            {syncAllResults && !syncAllProgress && (
              <div data-testid="perf-sync-all-results" className="mt-3 bg-[#0A0A0E] border border-[#26262B] rounded-sm">
                <div className="flex items-center gap-3 px-3 py-2 border-b border-[#26262B]">
                  <div className="text-[10px] font-mono font-bold uppercase tracking-[0.15em] text-white">
                    Sync All · {syncAllResults.total} accounts
                  </div>
                  <span className="text-[10px] font-mono text-[#00FF88]">{syncAllResults.committed} committed</span>
                  <span className="text-[10px] font-mono text-[#FFB800]">{syncAllResults.skipped} dup</span>
                  {syncAllResults.failed > 0 && (
                    <span className="text-[10px] font-mono text-[#FF3366]">{syncAllResults.failed} failed</span>
                  )}
                  <button
                    type="button"
                    data-testid="perf-sync-all-dismiss"
                    onClick={() => setSyncAllResults(null)}
                    className="ml-auto text-[9px] font-mono text-[#5F5F66] hover:text-white px-2 py-0.5"
                  >dismiss</button>
                </div>
                <div className="max-h-[200px] overflow-y-auto">
                  {syncAllResults.results.map((r, i) => (
                    <div
                      key={`${r.account_number}-${i}`}
                      data-testid={`perf-sync-all-row-${r.account_number}`}
                      className="flex items-center gap-3 px-3 py-1.5 border-b border-[#1A1A20] last:border-b-0 text-[10px] font-mono"
                    >
                      <span className={`px-1.5 py-0.5 rounded-sm uppercase tracking-[0.1em] text-[9px] ${r.status === "ok" ? "bg-[#00FF88]/15 text-[#00FF88]" : "bg-[#FF3366]/15 text-[#FF3366]"}`}>
                        {r.status === "ok" ? "ok" : "error"}
                      </span>
                      <span className="text-white">{r.account_number}</span>
                      <span className="text-[#5F5F66] truncate">{r.account_label}</span>
                      {r.connection_nickname && (
                        <span className="text-[#5F5F66] hidden md:inline">via {r.connection_nickname}</span>
                      )}
                      <span className="ml-auto text-right">
                        {r.status === "ok" ? (
                          <>
                            <span className="text-[#00FF88]">+{r.committed}</span>
                            {r.skipped > 0 && <span className="text-[#FFB800] ml-2">{r.skipped} dup</span>}
                          </>
                        ) : (
                          <span className="text-[#FF3366] truncate">{r.error}</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <div data-testid="perf-upload-zone"
          onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) doUpload(f); }}
          className={`border-2 border-dashed rounded-sm p-10 text-center transition-colors ${drag ? "border-[#00FF88] bg-[#00FF88]/5" : "border-[#26262B] bg-[#121216]"}`}>
          <Upload size={40} className="text-[#5F5F66] mx-auto mb-3" />
          <div className="text-sm font-mono text-white mb-1">Drag a CSV or XLSX here</div>
          <div className="text-[11px] font-mono text-[#8A8A93]">or</div>
          <button data-testid="perf-upload-btn" onClick={() => fileRef.current?.click()}
            className="mt-3 bg-[#00FF88] text-[#08080A] text-xs font-bold uppercase tracking-[0.15em] px-4 py-2 rounded-sm hover:bg-[#33FF9F] disabled:opacity-50">
            {uploading ? <Loader2 size={12} className="animate-spin inline" /> : "Choose File"}
          </button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
            onChange={e => e.target.files?.[0] && doUpload(e.target.files[0])} />
          <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
            <span className="text-[10px] font-mono text-[#8A8A93]">Source:</span>
            {["auto", "tastytrade", "tradier", "generic"].map(s => (
              <button key={s} data-testid={`perf-src-${s}`} onClick={() => setSource(s)}
                className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border ${source === s ? "border-[#00FF88] text-[#00FF88]" : "border-[#3F3F47] text-[#8A8A93]"}`}>{s}</button>
            ))}
          </div>
          {/* Iter119.19 — Optional: tag this CSV upload with a sub-account
              number so committed rows partition into the right pile. The
              dropdown lists every account already known to the system, plus
              "Unassigned" as the default. */}
          <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
            <span className="text-[10px] font-mono text-[#8A8A93]">Account:</span>
            <select
              data-testid="perf-upload-account"
              value={uploadAccount}
              onChange={e => setUploadAccount(e.target.value)}
              className="text-[10px] font-mono bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-2 py-0.5"
            >
              <option value="">Unassigned (combined view)</option>
              {(accounts || []).filter(a => a.account_number).map(a => (
                <option key={a.account_number} value={a.account_number}>
                  {a.nickname || a.account_number}
                </option>
              ))}
            </select>
            {uploadAccount && (
              <span className="text-[9px] font-mono text-[#00FF88]">→ rows tagged</span>
            )}
          </div>
          <div className="text-[10px] font-mono text-[#5F5F66] mt-3">✓ Re-uploading is safe — duplicates auto-skipped by Order # + signature.</div>
        </div>
        {error && (<div className="text-[11px] font-mono text-[#FF3366] bg-[#FF3366]/5 border border-[#FF3366]/20 px-3 py-2 rounded-sm">{error}</div>)}
        {committedToast && (
          <div className="text-[11px] font-mono text-[#00FF88] bg-[#00FF88]/5 border border-[#00FF88]/20 px-3 py-2 rounded-sm">
            ✓ Imported {committedToast.committed} new rows.
            {committedToast.skipped > 0 && <> Skipped {committedToast.skipped} duplicate{committedToast.skipped === 1 ? "" : "s"}.</>}
          </div>
        )}
        {preview && (
          <div data-testid="perf-preview" className="bg-[#121216] border border-[#26262B] rounded-sm p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-[11px] font-mono">
                <span className="text-[#8A8A93]">Detected: </span>
                <span className="text-[#00FF88] font-bold uppercase">{preview.source}</span>
                <span className="text-[#5F5F66] ml-2">· {preview.row_count_parsed}/{preview.row_count_raw} rows · {preview.filename}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setPreview(null)} className="text-[11px] font-mono text-[#8A8A93] hover:text-[#FF3366]">Cancel</button>
                <button data-testid="perf-commit-btn" onClick={commit} disabled={uploading}
                  className="bg-[#00FF88] text-[#08080A] text-[11px] font-bold px-3 py-1 rounded-sm hover:bg-[#33FF9F] disabled:opacity-40">
                  {uploading ? <Loader2 size={12} className="animate-spin inline" /> : "Commit (skip duplicates)"}
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[10px] font-mono">
                <thead><tr className="text-[#8A8A93] border-b border-[#26262B]">
                  <th className="text-left px-2 py-1">Date</th><th className="text-left px-2 py-1">Sym</th>
                  <th className="text-left px-2 py-1">Action</th><th className="text-right px-2 py-1">Qty</th>
                  <th className="text-right px-2 py-1">Strike</th><th className="text-right px-2 py-1">Amount</th></tr></thead>
                <tbody>
                  {(preview.sample || []).map((r, i) => (
                    <tr key={i} className="border-b border-[#1A1A20]">
                      <td className="px-2 py-1 text-white">{r.date?.slice(0, 10) || "—"}</td>
                      <td className="px-2 py-1 text-[#00FF88]">{r.symbol}</td>
                      <td className="px-2 py-1 text-[#8A8A93]">{r.action}</td>
                      <td className="px-2 py-1 text-right text-white">{r.quantity}</td>
                      <td className="px-2 py-1 text-right text-white">{r.strike ?? "—"}</td>
                      <td className={`px-2 py-1 text-right ${r.amount > 0 ? "text-[#00FF88]" : "text-[#FF3366]"}`}>{fmtUsd(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <div>
        <div className="text-[11px] font-mono font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-2">Import History</div>
        <div className="space-y-2" data-testid="perf-batches">
          {batches.length === 0 && <div className="text-[11px] font-mono text-[#5F5F66]">No imports yet.</div>}
          {batches.map(b => (
            <div key={b.id} className="bg-[#121216] border border-[#26262B] rounded-sm p-2.5 text-[10px] font-mono">
              <div className="flex items-center justify-between mb-1">
                <span className={`font-bold uppercase ${b.status === "committed" ? "text-[#00FF88]" : b.status === "preview" ? "text-[#FFB800]" : "text-[#8A8A93]"}`}>{b.status}</span>
                <button onClick={() => rollback(b.id)} className="text-[#8A8A93] hover:text-[#FF3366]"><Trash2 size={11} /></button>
              </div>
              <div className="text-white truncate">{b.filename}</div>
              <div className="text-[#5F5F66]">{b.source} · {b.committed_rows ?? b.row_count_parsed} rows
                {b.skipped_duplicates > 0 && <span className="text-[#FFB800]"> · skipped {b.skipped_duplicates} dup</span>}
                · {(b.created_at || "").slice(0, 10)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
    <ReconcileCard userParams={userParams} accounts={accounts} />
    </div>
  );
}

// ===========================================================================
// Iter119.28 — Reconcile against broker CSV
// READ-ONLY diff between an uploaded CSV and committed transactions.
// Categorises each row into 4 buckets so the operator can spot
// data-fidelity drift between sync ↔ CSV without writing to the DB.
// ===========================================================================
function ReconcileCard({ userParams, accounts }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [acct, setAcct] = useState("");

  const run = async () => {
    if (!file) return;
    setBusy(true); setError(null); setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", "tastytrade");
      if (acct) fd.append("account_number", acct);
      const params = { ...userParams() };
      const r = await axios.post(`${API}/perf/reconcile`, fd, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Reconcile failed");
    }
    setBusy(false);
  };

  return (
    <div data-testid="perf-reconcile-card" className="bg-[#121216] border border-[#26262B] rounded-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <FileSpreadsheet size={14} className="text-[#22D3FF]" />
        <div className="text-xs font-mono font-bold uppercase tracking-[0.15em] text-[#22D3FF]">
          Reconcile against broker CSV
        </div>
        <span className="text-[10px] font-mono text-[#5F5F66] ml-auto">read-only · no commit, no writes</span>
      </div>
      <div className="text-[10px] font-mono text-[#8A8A93] leading-relaxed mb-3">
        Drop in any Tastytrade transaction export. We&apos;ll diff it against your
        committed transactions row-by-row and report exactly what&apos;s
        <span className="text-[#00FF88]"> matched</span>,
        <span className="text-[#FFB800]"> mismatched in amount</span>,
        <span className="text-[#22D3FF]"> missing in the app</span>, or
        <span className="text-[#FF3366]"> extra in the app</span>.
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#8A8A93] mb-1">Broker CSV</div>
          <input
            data-testid="perf-reconcile-file"
            type="file"
            accept=".csv,text/csv"
            onChange={e => setFile(e.target.files?.[0] || null)}
            className="block text-[10px] font-mono text-[#8A8A93] file:bg-[#22D3FF]/10 file:text-[#22D3FF] file:border-0 file:px-3 file:py-1.5 file:rounded-sm file:font-bold file:uppercase file:tracking-[0.15em] file:text-[10px] hover:file:bg-[#22D3FF]/20"
          />
        </div>
        {accounts && accounts.length > 0 && (
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#8A8A93] mb-1">Scope to sub-account</div>
            <select
              data-testid="perf-reconcile-account"
              value={acct}
              onChange={e => setAcct(e.target.value)}
              className="bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono"
            >
              <option value="">all accounts</option>
              {accounts.map(a => (
                <option key={a.account_number || "_unassigned"} value={a.account_number || ""}>
                  {a.nickname || a.account_number || "Unassigned"}
                </option>
              ))}
            </select>
          </div>
        )}
        <button
          data-testid="perf-reconcile-run"
          onClick={run}
          disabled={!file || busy}
          className="bg-[#22D3FF] text-[#08080A] text-xs font-bold uppercase tracking-[0.15em] px-4 py-2 rounded-sm hover:bg-[#5BDDFF] disabled:opacity-40 flex items-center gap-2"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Repeat size={12} />}
          {busy ? "Reconciling…" : "Run reconciliation"}
        </button>
      </div>
      {error && (
        <div className="mt-3 bg-[#FF3366]/10 border border-[#FF3366]/40 rounded-sm px-3 py-2 text-[11px] font-mono text-[#FF3366]">
          {error}
        </div>
      )}
      {report && (
        <div className="mt-4 space-y-3" data-testid="perf-reconcile-report">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <ReconcileBucket testid="perf-reconcile-count-matched"    label="Matched"    count={report.counts.matched}            color="text-[#00FF88]" />
            <ReconcileBucket testid="perf-reconcile-count-mismatched" label="Mismatched $" count={report.counts.mismatched_amount} color="text-[#FFB800]" />
            <ReconcileBucket testid="perf-reconcile-count-missing"    label="Missing in app" count={report.counts.missing_in_app}   color="text-[#22D3FF]" />
            <ReconcileBucket testid="perf-reconcile-count-extra"      label="Extra in app"   count={report.counts.extra_in_app}     color="text-[#FF3366]" />
          </div>
          <div className="text-[10px] font-mono text-[#5F5F66]">
            CSV had {report.csv_row_count} rows · DB has {report.db_row_count} rows
            {report.account_number ? <> · scoped to <span className="text-[#22D3FF]">{report.account_number}</span></> : <> · all accounts</>}
            · source detected as <span className="text-white">{report.source_detected}</span>
          </div>
          {/* Inline triage tables for each non-empty bucket */}
          {report.counts.mismatched_amount > 0 && (
            <DiffTable
              testid="perf-reconcile-table-mismatched"
              title="Mismatched amounts"
              color="text-[#FFB800]"
              rows={report.samples.mismatched_amount}
              columns={[
                { label: "Date",   get: r => (r.date || "").slice(0, 10) },
                { label: "Symbol", get: r => r.symbol },
                { label: "Action", get: r => r.action },
                { label: "Strike", get: r => r.strike ?? "—" },
                { label: "Qty",    get: r => r.quantity },
                { label: "CSV $",  get: r => fmtUsd(r.csv_amount) },
                { label: "DB $",   get: r => fmtUsd(r.db_amount) },
              ]}
            />
          )}
          {report.counts.missing_in_app > 0 && (
            <DiffTable
              testid="perf-reconcile-table-missing"
              title="Missing in app (in CSV but not yet committed)"
              color="text-[#22D3FF]"
              rows={report.samples.missing_in_app}
              columns={[
                { label: "Date",   get: r => (r.date || "").slice(0, 10) },
                { label: "Symbol", get: r => r.symbol },
                { label: "Action", get: r => r.action },
                { label: "Strike", get: r => r.strike ?? "—" },
                { label: "Qty",    get: r => r.quantity },
                { label: "Amount", get: r => fmtUsd(r.amount) },
              ]}
            />
          )}
          {report.counts.extra_in_app > 0 && (
            <DiffTable
              testid="perf-reconcile-table-extra"
              title="Extra in app (in DB but absent from CSV)"
              color="text-[#FF3366]"
              rows={report.samples.extra_in_app}
              columns={[
                { label: "Date",   get: r => (r.date || "").slice(0, 10) },
                { label: "Symbol", get: r => r.symbol },
                { label: "Action", get: r => r.action },
                { label: "Strike", get: r => r.strike ?? "—" },
                { label: "Qty",    get: r => r.quantity },
                { label: "Amount", get: r => fmtUsd(r.amount) },
                { label: "Source", get: r => r.source || "—" },
              ]}
            />
          )}
          {report.counts.matched === report.csv_row_count && report.counts.extra_in_app === 0 && (
            <div className="bg-[#00FF88]/10 border border-[#00FF88]/40 rounded-sm px-3 py-2 text-[11px] font-mono text-[#00FF88]">
              Reconciled cleanly — every CSV row maps to a committed transaction and the DB has no extras.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReconcileBucket({ label, count, color, testid }) {
  return (
    <div data-testid={testid} className="bg-[#0A0A0E] border border-[#1A1A20] rounded-sm px-3 py-2">
      <div className={`text-[10px] font-mono uppercase tracking-[0.15em] ${color}`}>{label}</div>
      <div className="text-2xl font-bold text-white tabular-nums mt-0.5">{count}</div>
    </div>
  );
}

function DiffTable({ title, color, rows, columns, testid }) {
  return (
    <div className="bg-[#0A0A0E] border border-[#1A1A20] rounded-sm" data-testid={testid}>
      <div className={`px-3 py-2 text-[10px] font-mono font-bold uppercase tracking-[0.15em] border-b border-[#1A1A20] ${color}`}>
        {title} <span className="text-[#5F5F66] normal-case tracking-normal">· showing first {rows.length}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px] font-mono">
          <thead>
            <tr className="text-[#8A8A93] border-b border-[#1A1A20] uppercase tracking-[0.15em]">
              {columns.map((c) => (
                <th key={c.label} className="text-left px-3 py-1.5">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.external_id || r.date || ""}-${i}`} className="border-b border-[#1A1A20] last:border-b-0 hover:bg-[#121216]">
                {columns.map((c) => (
                  <td key={c.label} className="px-3 py-1 text-white">{c.get(r) ?? "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===========================================================================
// Transactions tab
// ===========================================================================
function TransactionsTab({ userParams }) {
  const [tx, setTx] = useState([]); const [loading, setLoading] = useState(false); const [symbol, setSymbol] = useState("");
  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/perf/transactions`, { params: { ...userParams(), ...(symbol ? { symbol } : {}), limit: 1000 } }); setTx(r.data); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [symbol]);
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <input data-testid="perf-tx-symbol-filter" type="text" placeholder="Filter by symbol…"
          value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())}
          className="bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-1.5 text-xs font-mono w-40" />
        <button onClick={load} className="text-[11px] font-mono px-2.5 py-1 rounded-sm border border-[#26262B] text-[#8A8A93] hover:text-white">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
        </button>
        <span className="text-[11px] font-mono text-[#5F5F66]">{tx.length} rows</span>
      </div>
      <div className="bg-[#121216] border border-[#26262B] rounded-sm overflow-x-auto">
        <table className="w-full text-[10px] font-mono" data-testid="perf-tx-table">
          <thead>
            <tr className="text-[#8A8A93] border-b border-[#26262B] uppercase tracking-[0.15em]">
              <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Src</th>
              <th className="text-left px-3 py-2">Symbol</th><th className="text-left px-3 py-2">Action</th>
              <th className="text-left px-3 py-2">Type</th><th className="text-right px-3 py-2">Qty</th>
              <th className="text-right px-3 py-2">Strike</th><th className="text-right px-3 py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {tx.length === 0 && (<tr><td colSpan={8} className="text-center py-8 text-[#5F5F66]">No transactions yet.</td></tr>)}
            {tx.map((r, i) => (
              <tr key={`${r.external_id || r.date || i}-${i}`} className="border-b border-[#1A1A20] hover:bg-[#0A0A0D]">
                <td className="px-3 py-1.5 text-white">{r.date?.slice(0, 10) || "—"}</td>
                <td className="px-3 py-1.5 text-[#5F5F66] uppercase">{r.source}</td>
                <td className="px-3 py-1.5 text-[#00FF88] font-bold">{r.symbol}</td>
                <td className="px-3 py-1.5 text-[#8A8A93]">{r.action}</td>
                <td className="px-3 py-1.5 text-[#5F5F66]">{r.option_type || r.instrument_type}</td>
                <td className="px-3 py-1.5 text-right text-white">{r.quantity}</td>
                <td className="px-3 py-1.5 text-right text-white">{r.strike ?? "—"}</td>
                <td className={`px-3 py-1.5 text-right ${r.amount > 0 ? "text-[#00FF88]" : "text-[#FF3366]"}`}>{fmtUsd(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===========================================================================
// Settings tab — account settings + Stoic thresholds
// Iter119.19 — Per-account settings: when a sub-account is selected in the
// header picker, the starting-capital / starting-date / basis save against
// THAT account. The Stoic thresholds remain user-wide (one risk profile
// per operator, applied across every sub-account). A new "Accounts" section
// lets the operator rename each Tastytrade sub-account inline.
// ===========================================================================
function SettingsTab({ userParams, account, accounts, onAccountsChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [settings, setSettings] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autoSync, setAutoSync] = useState(null);
  const [autoSavingSync, setAutoSavingSync] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoAccounts, setAutoAccounts] = useState([]);
  // Iter119.19 — Per-account params: omit account_number when the global
  // default is selected (account === null) so existing legacy data stays
  // addressable. Pass through whatever account the picker has chosen.
  const acctParams = () => ({
    ...userParams(),
    ...(account ? { account_number: account } : {}),
  });

  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API}/perf/account-settings`, { params: acctParams() }),
        axios.get(`${API}/perf/thresholds`, { params: userParams() }),
      ]);
      setSettings(s.data); setThresholds(t.data);
    } catch { setSettings({ starting_capital: 0 }); setThresholds(null); }
    if (isAdmin) {
      try {
        const [as, ac] = await Promise.all([
          axios.get(`${API}/perf/auto-sync/settings`),
          axios.get(`${API}/perf/sync/tastytrade/accounts`),
        ]);
        setAutoSync(as.data);
        setAutoAccounts(ac.data?.accounts || []);
      } catch { /* ignore */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [account, JSON.stringify(userParams())]);

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/perf/account-settings`, {
          starting_capital: Number(settings.starting_capital || 0),
          starting_date: settings.starting_date || null,
          default_basis: settings.default_basis || "fixed",
          nickname: settings.nickname || null,
        }, { params: acctParams() }),
        axios.put(`${API}/perf/thresholds`, {
          exposure_defense_pct: Number(thresholds.exposure_defense_pct),
          drawdown_defense_pct: Number(thresholds.drawdown_defense_pct),
          exposure_harvest_pct: Number(thresholds.exposure_harvest_pct),
          min_roi_gate_pct: Number(thresholds.min_roi_gate_pct),
        }, { params: userParams() }),
      ]);
      await load();
      // Iter119.19 — Notify the parent so the picker refreshes nicknames.
      onAccountsChanged && onAccountsChanged();
      toast.success(account ? "Account settings saved" : "Default settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const saveAutoSync = async () => {
    setAutoSavingSync(true);
    try {
      const r = await axios.put(`${API}/perf/auto-sync/settings`, {
        enabled: !!autoSync.enabled,
        account_number: autoSync.account_number || "",
        time_et: autoSync.time_et || "23:30",
      });
      setAutoSync(r.data);
      toast.success("Auto-sync settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setAutoSavingSync(false);
  };
  const runAutoSyncNow = async () => {
    setAutoRunning(true);
    try {
      const r = await axios.post(`${API}/perf/auto-sync/run-now`);
      setAutoSync(r.data);
      toast.success(`Auto-sync ran: ${r.data?.last_committed || 0} committed`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    setAutoRunning(false);
  };

  if (!settings || !thresholds) return <div className="text-[11px] font-mono text-[#5F5F66]">Loading…</div>;

  // Iter119.19 — Banner that clarifies which account's settings the operator
  // is editing right now. When `account === null`, settings save against the
  // user-global default ("All accounts").
  const scope = account === "unassigned"
    ? "Unassigned (legacy rows without an account_number)"
    : account
    ? (accounts.find(a => a.account_number === account)?.nickname || account)
    : "All accounts (global default)";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Iter119.19 — Editing scope banner. Tells the operator which row
          the upcoming save will write to (per-account or global default). */}
      <div data-testid="perf-settings-scope-banner" className="lg:col-span-2 flex items-center gap-2 px-3 py-2 rounded-sm border border-[#26262B] bg-[#0A0A0E] text-[10px] font-mono">
        <Wallet size={12} className="text-[#00FF88]" />
        <span className="text-[#8A8A93]">Editing settings for</span>
        <span className="text-[#00FF88] font-bold uppercase tracking-[0.12em]">{scope}</span>
        <span className="ml-auto text-[9px] text-[#5F5F66]">
          {account ? "Switch the account picker above to edit another." : "Pick a sub-account above to set its own capital + nickname."}
        </span>
      </div>

      <div className="bg-[#121216] border border-[#26262B] rounded-sm p-5 space-y-4">
        <div className="text-[11px] font-mono font-bold uppercase tracking-[0.15em] text-[#8A8A93]">Account</div>
        {/* Iter119.19 — Nickname field. Only meaningful when an account_number
            is selected; for the global default we hide it (it would never
            apply to a real account). */}
        {account && account !== "unassigned" && (
          <Field label="Nickname (e.g. Wife's IRA)">
            <input type="text" maxLength={40} data-testid="perf-nickname"
              value={settings.nickname || ""}
              onChange={e => setSettings({ ...settings, nickname: e.target.value })}
              placeholder="Personal · Spouse · Child 1 · ..."
              className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2 text-sm font-mono" />
          </Field>
        )}
        <Field label="Starting Capital (USD)">
          <input type="number" step="100" data-testid="perf-starting-capital" value={settings.starting_capital || ""}
            onChange={e => setSettings({ ...settings, starting_capital: e.target.value })}
            className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2 text-sm font-mono" />
        </Field>
        <Field label="Starting Date">
          <input type="date" data-testid="perf-starting-date" value={(settings.starting_date || "").slice(0, 10)}
            onChange={e => setSettings({ ...settings, starting_date: e.target.value ? `${e.target.value}T00:00:00Z` : null })}
            className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2 text-sm font-mono" />
        </Field>
      </div>

      <div className="bg-[#121216] border border-[#26262B] rounded-sm p-5 space-y-4">
        <div className="text-[11px] font-mono font-bold uppercase tracking-[0.15em] text-[#8A8A93]">Stoic Risk State · Thresholds</div>
        <Field label="Open Exposure → DEFENSE (%)">
          <input type="number" step="1" data-testid="thr-exposure-defense"
            value={thresholds.exposure_defense_pct}
            onChange={e => setThresholds({ ...thresholds, exposure_defense_pct: e.target.value })}
            className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2 text-sm font-mono" />
        </Field>
        <Field label="Max Drawdown → DEFENSE (%)">
          <input type="number" step="1" data-testid="thr-drawdown-defense"
            value={thresholds.drawdown_defense_pct}
            onChange={e => setThresholds({ ...thresholds, drawdown_defense_pct: e.target.value })}
            className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2 text-sm font-mono" />
        </Field>
        <Field label="Open Exposure ≤ → HARVEST (%)">
          <input type="number" step="1" data-testid="thr-exposure-harvest"
            value={thresholds.exposure_harvest_pct}
            onChange={e => setThresholds({ ...thresholds, exposure_harvest_pct: e.target.value })}
            className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2 text-sm font-mono" />
        </Field>
        <Field label="Minimum ROI Gate (%)">
          <input type="number" step="0.1" data-testid="thr-min-roi"
            value={thresholds.min_roi_gate_pct}
            onChange={e => setThresholds({ ...thresholds, min_roi_gate_pct: e.target.value })}
            className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2 text-sm font-mono" />
        </Field>
      </div>

      {/* Iter119.20 — Connected Brokers card. Lists every broker
          connection the operator owns; lets them add / test / delete /
          rename. Replaces the admin-global single-credential model from
          earlier iterations. */}
      <ConnectedBrokersCard />

      {isAdmin && autoSync && (
        <div data-testid="perf-autosync-card" className="lg:col-span-2 bg-gradient-to-br from-[#00FF88]/5 to-[#121216] border border-[#00FF88]/30 rounded-sm p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Zap size={16} className="text-[#00FF88]" />
            <div className="text-xs font-mono font-bold uppercase tracking-[0.15em] text-[#00FF88]">Tastytrade Auto-Sync</div>
            <span className="text-[10px] font-mono text-[#5F5F66] ml-auto">runs Mon-Fri at your configured time · US/Eastern</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <Field label="Status">
              <button data-testid="autosync-enabled-toggle"
                onClick={() => setAutoSync({ ...autoSync, enabled: !autoSync.enabled })}
                className={`w-full px-3 py-2 rounded-sm text-xs font-bold font-mono uppercase tracking-[0.15em] border ${
                  autoSync.enabled ? "bg-[#00FF88] text-[#08080A] border-[#00FF88]" : "bg-[#08080A] text-[#8A8A93] border-[#3F3F47]"}`}>
                {autoSync.enabled ? "ENABLED" : "DISABLED"}
              </button>
            </Field>
            <Field label="Account">
              <select data-testid="autosync-account" value={autoSync.account_number || ""}
                onChange={e => setAutoSync({ ...autoSync, account_number: e.target.value })}
                className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2 text-sm font-mono">
                <option value="">— select —</option>
                {autoAccounts.map(a => (<option key={a.account_number} value={a.account_number}>{a.account_number}</option>))}
              </select>
            </Field>
            <Field label="Time (HH:MM ET)">
              <input type="time" data-testid="autosync-time" value={autoSync.time_et || "23:30"}
                onChange={e => setAutoSync({ ...autoSync, time_et: e.target.value })}
                className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2 text-sm font-mono" />
            </Field>
            <div className="flex gap-2">
              <button data-testid="autosync-save" onClick={saveAutoSync} disabled={autoSavingSync}
                className="flex-1 bg-[#00FF88] text-[#08080A] text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-2 rounded-sm hover:bg-[#33FF9F] disabled:opacity-40">
                {autoSavingSync ? <Loader2 size={12} className="animate-spin inline" /> : "Save"}
              </button>
              <button data-testid="autosync-run-now" onClick={runAutoSyncNow} disabled={autoRunning}
                className="flex-1 bg-[#08080A] border border-[#3F3F47] text-white text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-2 rounded-sm hover:border-[#00FF88] disabled:opacity-40">
                {autoRunning ? <Loader2 size={12} className="animate-spin inline" /> : "Run Now"}
              </button>
            </div>
          </div>
          <div className="text-[10px] font-mono text-[#8A8A93] border-t border-[#26262B] pt-2">
            Last run:{" "}
            {autoSync.last_run_at
              ? <span className="text-white">{new Date(autoSync.last_run_at).toLocaleString()}</span>
              : <span className="text-[#5F5F66]">never</span>}
            {autoSync.last_status === "ok" && (
              <span className="text-[#00FF88]"> · committed {autoSync.last_committed} · skipped {autoSync.last_skipped} duplicates</span>
            )}
            {autoSync.last_status === "error" && (
              <span className="text-[#FF3366]"> · error: {autoSync.last_error}</span>
            )}
          </div>
        </div>
      )}

      {/* Iter119.19 — Per-account nicknames table. Lets the operator
          rename each Tastytrade sub-account inline (e.g. "•••12345" →
          "Wife's IRA"), set per-account starting capital, and see at a
          glance how many transactions land in each. Always visible
          (even with one account) so the multi-account model is
          discoverable. */}
      {accounts && accounts.length > 0 && (
        <AccountsCard accounts={accounts} userParams={userParams} onChanged={onAccountsChanged} />
      )}

      <div className="lg:col-span-2">
        <button data-testid="perf-settings-save" onClick={save} disabled={saving}
          className="w-full bg-[#00FF88] text-[#08080A] text-xs font-bold uppercase tracking-[0.15em] px-4 py-3 rounded-sm hover:bg-[#33FF9F] disabled:opacity-50">
          {saving ? <Loader2 size={12} className="animate-spin inline" /> : "Save All Settings"}
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
// Iter119.19 — Per-Account Settings Table
// ===========================================================================
//
// Lists every discovered Tastytrade sub-account in a compact table with
// inline-editable nickname + starting capital. Saving a row sends a PUT
// to /perf/account-settings?account_number=... with just that account's
// fields. Calls `onChanged()` so the parent re-fetches /perf/accounts
// (refreshes the pill row nicknames).
function AccountsCard({ accounts, userParams, onChanged }) {
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({ nickname: "", starting_capital: "" });
  const [savingRow, setSavingRow] = useState(false);
  // Iter119.26 — Track which row is being destructively wiped + the
  // nuclear "wipe all" busy state, both gated by typed confirmation.
  const [wipingId, setWipingId] = useState(null);
  const [wipingAll, setWipingAll] = useState(false);

  const startEdit = (acct) => {
    setEditId(acct.account_number || "_unassigned");
    setDraft({
      nickname: acct.nickname || "",
      starting_capital: acct.starting_capital || 0,
    });
  };
  const cancelEdit = () => { setEditId(null); setDraft({ nickname: "", starting_capital: "" }); };
  const saveRow = async (acct) => {
    setSavingRow(true);
    try {
      const params = { ...userParams() };
      if (acct.account_number) params.account_number = acct.account_number;
      await axios.put(`${API}/perf/account-settings`, {
        starting_capital: Number(draft.starting_capital || 0),
        starting_date: null,
        default_basis: "fixed",
        nickname: draft.nickname || null,
      }, { params });
      toast.success(`Saved · ${draft.nickname || acct.account_number || "default"}`);
      cancelEdit();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSavingRow(false);
  };

  // Iter119.26 — Per-row wipe: deletes that sub-account's transactions,
  // settings and batches via DELETE /perf/accounts/{account_number}.
  // Requires the operator to type the exact label as confirmation so
  // an accidental click on a populated row can't destroy data.
  const wipeRow = async (acct) => {
    const label = acct.nickname || (acct.account_number ? `•••${String(acct.account_number).slice(-5)}` : "Unassigned");
    const expected = acct.nickname || acct.account_number || "Unassigned";
    const typed = window.prompt(
      `Wipe sub-account "${label}"?\n\nThis deletes ${acct.txn_count || 0} transaction(s), its starting-capital row, and any pending import batches. The broker connection itself is NOT touched.\n\nType "${expected}" to confirm:`
    );
    if (typed == null) return;
    if (typed.trim() !== expected) {
      toast.error("Confirmation didn't match — nothing was deleted.");
      return;
    }
    setWipingId(acct.account_number || "_unassigned");
    try {
      const params = { ...userParams() };
      const slug = acct.account_number || "_unassigned";
      const r = await axios.delete(`${API}/perf/accounts/${encodeURIComponent(slug)}`, { params });
      toast.success(`Wiped "${label}" · ${r.data?.transactions_deleted || 0} txns · ${r.data?.batches_deleted || 0} batches removed`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
    setWipingId(null);
  };

  // Iter119.26 — Nuclear option: wipes EVERY transaction / batch /
  // setting for this user. Two-step confirmation: an opening
  // window.confirm() then a typed-string prompt asking for "WIPE".
  const wipeAll = async () => {
    if (!window.confirm("Wipe ALL Performance data for this profile?\n\nThis deletes every imported transaction, every batch, and every sub-account starting-capital row. Broker connections are NOT deleted.\n\nClick OK to see the typed confirmation.")) return;
    const typed = window.prompt('Type WIPE (uppercase) to confirm. Anything else cancels:');
    if ((typed || "").trim() !== "WIPE") {
      toast.error("Confirmation didn't match — nothing was deleted.");
      return;
    }
    setWipingAll(true);
    try {
      const params = { ...userParams(), confirm: "WIPE" };
      const r = await axios.post(`${API}/perf/wipe`, null, { params });
      toast.success(`Profile wiped · ${r.data?.transactions_deleted || 0} txns · ${r.data?.batches_deleted || 0} batches · ${r.data?.settings_deleted || 0} settings removed`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    }
    setWipingAll(false);
  };

  const mask = (acct) => {
    if (!acct) return "Unassigned";
    const s = String(acct);
    return s.length > 5 ? `•••${s.slice(-5)}` : s;
  };

  return (
    <div data-testid="perf-accounts-card" className="lg:col-span-2 bg-[#121216] border border-[#26262B] rounded-sm p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Wallet size={14} className="text-[#00FF88]" />
        <div className="text-xs font-mono font-bold uppercase tracking-[0.15em] text-[#8A8A93]">
          Sub-Accounts <span className="text-[#5F5F66] tracking-normal normal-case">· {accounts.length} discovered</span>
        </div>
        <span className="ml-auto text-[9px] font-mono text-[#5F5F66]">
          Rename a Tastytrade sub-account (e.g. &ldquo;Wife&rsquo;s IRA&rdquo;) so the picker reads naturally.
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px] font-mono">
          <thead>
            <tr className="text-[#8A8A93] border-b border-[#26262B] uppercase tracking-[0.15em]">
              <th className="text-left px-3 py-2">Account</th>
              <th className="text-left px-3 py-2">Nickname</th>
              <th className="text-right px-3 py-2">Starting Capital</th>
              <th className="text-right px-3 py-2">Transactions</th>
              <th className="text-right px-3 py-2">Last Activity</th>
              <th className="text-right px-3 py-2">·</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map(a => {
              const key = a.account_number || "_unassigned";
              const isEditing = editId === key;
              return (
                <tr key={key} data-testid={`perf-accounts-row-${a.account_number || "unassigned"}`}
                    className="border-b border-[#1A1A20] hover:bg-[#0A0A0D]">
                  <td className="px-3 py-1.5 text-white">{mask(a.account_number)}</td>
                  <td className="px-3 py-1.5 text-[#00FF88]">
                    {isEditing ? (
                      <input type="text" maxLength={40}
                        data-testid={`perf-accounts-nickname-input-${a.account_number || "unassigned"}`}
                        value={draft.nickname}
                        onChange={e => setDraft({ ...draft, nickname: e.target.value })}
                        autoFocus
                        className="w-full bg-[#08080A] border border-[#00FF88] focus:outline-none text-white rounded-sm px-2 py-1 text-[10px]" />
                    ) : (a.nickname || <span className="text-[#5F5F66] italic">— click pencil to name —</span>)}
                  </td>
                  <td className="px-3 py-1.5 text-right text-white">
                    {isEditing ? (
                      <input type="number" step="100"
                        data-testid={`perf-accounts-capital-input-${a.account_number || "unassigned"}`}
                        value={draft.starting_capital}
                        onChange={e => setDraft({ ...draft, starting_capital: e.target.value })}
                        className="w-24 bg-[#08080A] border border-[#00FF88] focus:outline-none text-white rounded-sm px-2 py-1 text-[10px] text-right" />
                    ) : fmtUsd(a.starting_capital)}
                  </td>
                  <td className="px-3 py-1.5 text-right text-[#8A8A93]">{a.txn_count}</td>
                  <td className="px-3 py-1.5 text-right text-[#5F5F66]">{a.last_date ? a.last_date.slice(0, 10) : "—"}</td>
                  <td className="px-3 py-1.5 text-right">
                    {isEditing ? (
                      <div className="inline-flex gap-1">
                        <button
                          data-testid={`perf-accounts-save-${a.account_number || "unassigned"}`}
                          onClick={() => saveRow(a)}
                          disabled={savingRow}
                          title="Save"
                          className="p-1 rounded-sm border border-[#00FF88]/40 text-[#00FF88] hover:bg-[#00FF88]/15 disabled:opacity-40">
                          {savingRow ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                        </button>
                        <button
                          onClick={cancelEdit}
                          title="Cancel"
                          className="p-1 rounded-sm border border-[#26262B] text-[#8A8A93] hover:text-white">
                          <Trash2 size={10} />
                        </button>
                      </div>
                    ) : (
                      <div className="inline-flex gap-1">
                        <button
                          data-testid={`perf-accounts-edit-${a.account_number || "unassigned"}`}
                          onClick={() => startEdit(a)}
                          title="Edit nickname + capital"
                          className="p-1 rounded-sm border border-transparent text-[#8A8A93] hover:text-[#00FF88] hover:border-[#00FF88]/40">
                          <Pencil size={10} />
                        </button>
                        <button
                          data-testid={`perf-accounts-wipe-${a.account_number || "unassigned"}`}
                          onClick={() => wipeRow(a)}
                          disabled={wipingId === (a.account_number || "_unassigned") || wipingAll}
                          title={`Wipe this sub-account (${a.txn_count || 0} txns)`}
                          className="p-1 rounded-sm border border-transparent text-[#8A8A93] hover:text-[#FF3366] hover:border-[#FF3366]/40 disabled:opacity-40">
                          {wipingId === (a.account_number || "_unassigned") ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-[#26262B]">
        <div className="text-[9px] font-mono text-[#5F5F66] leading-relaxed">
          <span className="text-[#FF3366]">Danger zone</span> — wiping deletes transactions, batches and starting-capital rows. Broker connections stay.
        </div>
        <button
          type="button"
          data-testid="perf-accounts-wipe-all"
          onClick={wipeAll}
          disabled={wipingAll || wipingId != null || accounts.length === 0}
          className="bg-transparent border border-[#FF3366]/60 text-[#FF3366] text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm hover:bg-[#FF3366]/10 disabled:opacity-40 flex items-center gap-1.5"
          title="Wipe ALL Performance data for this profile (broker connections survive)"
        >
          {wipingAll ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
          {wipingAll ? "Wiping…" : "Wipe All Data"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#8A8A93] block mb-1">{label}</label>
      {children}
    </div>
  );
}


// ===========================================================================
// Iter119.20 — Connected Brokers card
// ===========================================================================
//
// Lists every broker connection owned by the current user. Each row shows
// the broker icon, nickname, status badge, last-synced timestamp, and a
// row of actions (Test · Edit · Delete). Adding a connection opens an
// inline form that adapts to the chosen broker (Tastytrade needs three
// fields; Tradier needs one).
function ConnectedBrokersCard() {
  const [conns, setConns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    broker: "tastytrade",
    nickname: "",
    client_id: "",
    client_secret: "",
    refresh_token: "",
    api_key: "",
    sandbox: false,
  });
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/connections`);
      setConns(r.data || []);
    } catch { setConns([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Iter119.21 — OAuth callback bounces back here with ?tt_connected=1 or
  // ?tt_error=... on the Performance route. Surface a toast and clean the
  // URL so the operator sees the outcome inline.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ok = params.get("tt_connected");
    const err = params.get("tt_error");
    if (!ok && !err) return;
    if (ok) toast.success("Tastytrade connected — refresh token saved");
    if (err) toast.error(`Tastytrade OAuth failed: ${err}`);
    // Strip the query string so a reload doesn't re-fire the toast.
    const clean = window.location.pathname + window.location.hash;
    window.history.replaceState({}, "", clean);
  }, []);

  const resetForm = () => setForm({
    broker: "tastytrade", nickname: "",
    client_id: "", client_secret: "", refresh_token: "",
    api_key: "", sandbox: false,
  });

  const create = async () => {
    try {
      await axios.post(`${API}/connections`, form);
      toast.success(`Connection "${form.nickname}" added`);
      setAddOpen(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add connection");
    }
  };

  const saveEdit = async (id) => {
    try {
      // Only send fields the operator filled in; empty string means
      // "don't change". The backend skips empty credential fields.
      const payload = {
        broker: form.broker,
        nickname: form.nickname,
        client_id: form.client_id,
        client_secret: form.client_secret,
        refresh_token: form.refresh_token,
        api_key: form.api_key,
        sandbox: form.sandbox,
      };
      await axios.put(`${API}/connections/${id}`, payload);
      toast.success("Connection updated");
      setEditingId(null);
      resetForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      broker: c.broker,
      nickname: c.nickname || "",
      client_id: c.client_id || "",
      client_secret: "",  // never pre-fill secrets
      refresh_token: "",
      api_key: "",
      sandbox: !!c.sandbox,
    });
    setAddOpen(false);
  };

  const test = async (id) => {
    setBusyId(id);
    try {
      const r = await axios.post(`${API}/connections/${id}/test`);
      if (r.data.status === "healthy") {
        toast.success(`Connection healthy — ${(r.data.accounts || []).length} sub-account(s) discovered`);
      } else {
        toast.error(`Connection broken: ${r.data.last_error || "unknown error"}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    }
    setBusyId(null);
  };

  // Iter119.21 — Per-connection OAuth redirect. Asks the backend to mint
  // an authorize URL that carries this connection_id in the state param,
  // then redirects the browser. The callback will write the freshly-
  // minted refresh_token back into THIS connection row.
  const connectViaOAuth = async (id) => {
    try {
      // Iter119.33 — pass our origin explicitly (see SettingsPage.js).
      const redirectUri = `${window.location.origin}/api/oauth/tastytrade/callback`;
      const r = await axios.get(`${API}/oauth/tastytrade/authorize`, {
        params: { connection_id: id, redirect_uri: redirectUri },
      });
      if (r?.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("OAuth authorize endpoint returned no URL");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start OAuth");
    }
  };

  const del = async (id, nickname) => {
    if (!window.confirm(`Delete connection "${nickname}"? This will NOT delete the transactions already imported from it.`)) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/connections/${id}`);
      toast.success(`Connection "${nickname}" deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
    setBusyId(null);
  };

  const STATUS_BADGE = {
    healthy:      { color: "#00FF88", label: "Healthy"      },
    migrated:     { color: "#FFB800", label: "Migrated"     },
    never_tested: { color: "#5F5F66", label: "Untested"     },
    broken:       { color: "#FF3366", label: "Broken"       },
    expiring:     { color: "#FFB800", label: "Expiring"     },
  };

  return (
    <div data-testid="connected-brokers-card" className="lg:col-span-2 bg-[#121216] border border-[#26262B] rounded-sm p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Users size={14} className="text-[#22D3FF]" />
        <div className="text-xs font-mono font-bold uppercase tracking-[0.15em] text-[#22D3FF]">
          Connected Brokers <span className="text-[#5F5F66] tracking-normal normal-case">· {conns.length}</span>
        </div>
        <button
          type="button"
          data-testid="connection-add-button"
          onClick={() => { setAddOpen(o => !o); setEditingId(null); resetForm(); }}
          className="ml-auto text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-sm border border-[#22D3FF]/40 text-[#22D3FF] hover:bg-[#22D3FF]/10"
        >
          {addOpen ? "Cancel" : "+ Add Connection"}
        </button>
      </div>

      {loading && <div className="text-[10px] font-mono text-[#5F5F66]">Loading…</div>}

      {/* Inline add/edit form */}
      {(addOpen || editingId) && (
        <ConnectionForm
          form={form}
          setForm={setForm}
          editing={!!editingId}
          onSubmit={() => editingId ? saveEdit(editingId) : create()}
        />
      )}

      {/* List */}
      <div className="space-y-1">
        {conns.length === 0 && !loading && (
          <div className="text-[11px] font-mono text-[#5F5F66] italic px-2 py-3 text-center">
            No broker connections yet. Click &ldquo;+ Add Connection&rdquo; to wire up Tastytrade or Tradier.
          </div>
        )}
        {conns.map(c => {
          const badge = STATUS_BADGE[c.status] || STATUS_BADGE.never_tested;
          const isEditing = editingId === c.id;
          return (
            <div
              key={c.id}
              data-testid={`connection-row-${c.id}`}
              className={`flex items-center gap-3 px-3 py-2 rounded-sm border ${isEditing ? "border-[#FFB800]/40 bg-[#FFB800]/5" : "border-[#26262B] bg-[#0A0A0E]"}`}
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-white">
                {c.broker === "tastytrade" ? "TT" : "TR"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold text-white truncate">{c.nickname || "(unnamed)"}</div>
                <div className="text-[9px] font-mono text-[#5F5F66] truncate">
                  {c.broker} · {c.broker === "tastytrade" ? c.refresh_token_masked : c.api_key_masked}
                  {c.last_synced && <> · synced {new Date(c.last_synced).toLocaleString()}</>}
                </div>
                {c.last_error && (
                  <div className="text-[9px] font-mono text-[#FF3366] truncate mt-0.5">⚠ {c.last_error}</div>
                )}
              </div>
              <div
                data-testid={`connection-badge-${c.id}`}
                className="text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm"
                style={{ background: `${badge.color}22`, color: badge.color }}
              >
                {badge.label}
              </div>
              <div className="flex gap-1">
                {c.broker === "tastytrade" && (
                  <button
                    type="button"
                    data-testid={`connection-oauth-${c.id}`}
                    onClick={() => connectViaOAuth(c.id)}
                    disabled={busyId === c.id || !c.client_id}
                    title={c.client_id ? "Connect / refresh via Tastytrade OAuth" : "Add a Client ID to this connection first"}
                    className="text-[9px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-sm border border-[#22D3FF]/40 text-[#22D3FF] hover:bg-[#22D3FF]/15 disabled:opacity-40 inline-flex items-center gap-1"
                  >
                    <Link2 size={9} /> OAuth
                  </button>
                )}
                <button
                  type="button"
                  data-testid={`connection-test-${c.id}`}
                  onClick={() => test(c.id)}
                  disabled={busyId === c.id}
                  className="text-[9px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-sm border border-[#00FF88]/40 text-[#00FF88] hover:bg-[#00FF88]/15 disabled:opacity-40"
                >
                  {busyId === c.id ? <Loader2 size={9} className="animate-spin" /> : "Test"}
                </button>
                <button
                  type="button"
                  data-testid={`connection-edit-${c.id}`}
                  onClick={() => isEditing ? (setEditingId(null), resetForm()) : startEdit(c)}
                  className={`p-1 rounded-sm border ${isEditing ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}
                >
                  <Pencil size={10} />
                </button>
                <button
                  type="button"
                  data-testid={`connection-delete-${c.id}`}
                  onClick={() => del(c.id, c.nickname)}
                  disabled={busyId === c.id}
                  className="p-1 rounded-sm border border-transparent text-[#8A8A93] hover:text-[#FF3366] hover:border-[#FF3366]/40"
                >
                  <Trash2 size={10} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[9px] font-mono text-[#5F5F66] italic">
        Each connection is independent — credentials, sub-accounts, KPIs. Adding a second Tastytrade login brings in its sub-accounts on next sync.
      </div>
    </div>
  );
}

// Inline form used both for &quot;Add&quot; and &quot;Edit&quot; modes. Renders different
// credential fields based on the chosen broker.
function ConnectionForm({ form, setForm, editing, onSubmit }) {
  const tt = form.broker === "tastytrade";
  return (
    <div
      data-testid="connection-form"
      className="bg-[#08080A] border border-[#26262B] rounded-sm p-4 space-y-3"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Broker">
          <select
            value={form.broker}
            onChange={e => setForm({ ...form, broker: e.target.value })}
            disabled={editing}
            data-testid="connection-form-broker"
            className="w-full bg-[#0A0A0E] border border-[#26262B] focus:border-[#22D3FF] text-white rounded-sm px-3 py-2 text-xs font-mono disabled:opacity-50"
          >
            <option value="tastytrade">Tastytrade</option>
            <option value="tradier">Tradier</option>
          </select>
        </Field>
        <Field label={tt ? "Nickname (e.g. AEQUITAS Family)" : "Nickname (e.g. Tradier Personal)"}>
          <input
            type="text"
            maxLength={40}
            data-testid="connection-form-nickname"
            value={form.nickname}
            onChange={e => setForm({ ...form, nickname: e.target.value })}
            placeholder="Personal · Spouse · Family Brokerage..."
            className="w-full bg-[#0A0A0E] border border-[#26262B] focus:border-[#22D3FF] text-white rounded-sm px-3 py-2 text-xs font-mono"
          />
        </Field>
      </div>

      {tt ? (
        <>
          <Field label={editing ? "Client ID" : "Client ID (required)"}>
            <input
              type="text"
              data-testid="connection-form-client-id"
              value={form.client_id}
              onChange={e => setForm({ ...form, client_id: e.target.value })}
              className="w-full bg-[#0A0A0E] border border-[#26262B] focus:border-[#22D3FF] text-white rounded-sm px-3 py-2 text-xs font-mono"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={editing ? "Client Secret (leave blank to keep)" : "Client Secret (required)"}>
              <input
                type="password"
                data-testid="connection-form-client-secret"
                value={form.client_secret}
                onChange={e => setForm({ ...form, client_secret: e.target.value })}
                className="w-full bg-[#0A0A0E] border border-[#26262B] focus:border-[#22D3FF] text-white rounded-sm px-3 py-2 text-xs font-mono"
              />
            </Field>
            <Field label={editing ? "Refresh Token (leave blank to keep)" : "Refresh Token (optional — OAuth fills this)"}>
              <input
                type="password"
                data-testid="connection-form-refresh-token"
                value={form.refresh_token}
                onChange={e => setForm({ ...form, refresh_token: e.target.value })}
                placeholder="Click OAuth on the row to auto-fill"
                className="w-full bg-[#0A0A0E] border border-[#26262B] focus:border-[#22D3FF] text-white rounded-sm px-3 py-2 text-xs font-mono placeholder:text-[#3F3F46]"
              />
            </Field>
          </div>
          <div className="text-[9px] font-mono text-[#5F5F66] leading-relaxed">
            Recommended: save Client ID + Client Secret, then hit the cyan
            <span className="text-[#22D3FF]"> OAuth</span> button on the row to authorize Tastytrade
            without ever pasting a refresh token by hand.
          </div>
          <label className="flex items-center gap-2 text-[10px] font-mono text-[#8A8A93]">
            <input
              type="checkbox"
              data-testid="connection-form-sandbox"
              checked={!!form.sandbox}
              onChange={e => setForm({ ...form, sandbox: e.target.checked })}
            />
            Sandbox (cert.tastyworks.com)
          </label>
        </>
      ) : (
        <Field label={editing ? "API Key (leave blank to keep)" : "API Key"}>
          <input
            type="password"
            data-testid="connection-form-api-key"
            value={form.api_key}
            onChange={e => setForm({ ...form, api_key: e.target.value })}
            className="w-full bg-[#0A0A0E] border border-[#26262B] focus:border-[#22D3FF] text-white rounded-sm px-3 py-2 text-xs font-mono"
          />
        </Field>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          data-testid="connection-form-submit"
          onClick={onSubmit}
          disabled={!form.nickname.trim()}
          className="flex-1 bg-[#22D3FF] text-[#08080A] text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-2 rounded-sm hover:bg-[#5BDFFF] disabled:opacity-40"
        >
          {editing ? "Save changes" : "Create connection"}
        </button>
      </div>
    </div>
  );
}
